// src/components/dashboards/FinanceDashboard.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../shared/DashboardLayout';
import StatCard from '../shared/StatCard';
import { financeAPI, payrollAPI, labAPI, hostelInventoryAPI, adminAPI, expenseAPI, vehicleExpenseAPI } from '../../services/api';
import HostelUserPortalTab from '../hostel/HostelUserPortalTab';
import '../../styles/Dashboard.css';
import '../../styles/FinanceDashboard.css';
import '../../styles/PayrollManagement.css';
import DailyExpensesTab from './DailyExpensesTab';
import HospitalExpensesTab from './HospitalExpensesTab';
import SalaryStructures from './SalaryStructures';
import FeeStructures from './FeeStructures';
import ApprovePaySlip from '../admin/ApprovePaySlip';
import TransportFinanceExpensesTab from '../finance/TransportFinanceExpensesTab';
import Calendar from '../shared/Calendar';
import { notify, confirmDialog } from '../../utils/notify';


/* ─── constants ──────────────────────────────────────────────────────────── */

const ALL_PAYROLL_ROLES = [
  { value: 'FACULTY',               label: 'Faculty' },
  { value: 'FINANCE',               label: 'Finance' },
  { value: 'HOSTEL_ADMIN',          label: 'Hostel Admin' },
  { value: 'LIBRARY_STAFF',         label: 'Library Staff' },
  { value: 'TRANSPORT_MANAGER',     label: 'Transport Manager' },
  { value: 'recepsonist',           label: 'Receptionist' },
  { value: 'LAB_TECH_MICROBIOLOGY', label: 'Lab Tech – Microbiology' },
  { value: 'LAB_TECH_PATHOLOGY',    label: 'Lab Tech – Pathology' },
  { value: 'LAB_TECH_BIOCHEMISTRY', label: 'Lab Tech – Biochemistry' },
  { value: 'BILLING_MEMBER',        label: 'Billing Member' },
  { value: 'BILLING_MANAGER',       label: 'Billing Manager' },
  { value: 'SAMPLE_COLLECTOR',      label: 'Sample Collector' },
  { value: 'REPORT_GENERATION',     label: 'Report Generation' },
  { value: 'GRIEVANCE_OFFICER',     label: 'Grievance Officer' },
  { value: 'PRINCIPAL',             label: 'Principal' },
  { value: 'DEAN',                  label: 'Dean' },
];

/* ─── helpers ────────────────────────────────────────────────────────────── */

function isoDate(d) { return d.toISOString().slice(0, 10); }

function monthBounds(year, month) {
  return {
    from: isoDate(new Date(year, month - 1, 1)),
    to:   isoDate(new Date(year, month, 0)),
  };
}

const NOW = new Date();
const DEFAULT_BOUNDS = monthBounds(NOW.getFullYear(), NOW.getMonth() + 1);

/* ─── CSV export ─────────────────────────────────────────────────────────── */

const generatePayrollCSV = (records) => {
  const headers = [
    'Employee Name','Employee ID','Role','Department',
    'From Date','To Date','Working Days',
    'Base Monthly Salary','Period Gross Salary','Total Deductions','Net Salary',
    'Account Holder','Bank Name','Account Number','IFSC','Branch','Status',
  ];
  const rows = records.map(r => {
    const b = r.user_bank_detail || {};
    return [
      r.user_name||'', r.user_employee_id||'', r.user_role||'', r.user_department||'',
      r.from_date||'', r.to_date||'', r.working_days||'',
      r.base_salary||'0', r.gross_salary||'0', r.total_deductions||'0', r.net_salary||'0',
      b.account_holder_name||'', b.bank_name||'', b.account_number||'', b.ifsc_code||'', b.branch||'',
      r.status||'',
    ];
  });
  const csv = [headers, ...rows]
    .map(row => row.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `payroll_${Date.now()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

/* ═══════════════════════════ MAIN COMPONENT ═════════════════════════════════ */

const FinanceDashboard = () => {
  const { user }              = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [hasHostelAllocation, setHasHostelAllocation] = useState(false);
  
  // Hostel Procurement States
  const [hostelPurchaseRequests, setHostelPurchaseRequests] = useState([]);
  const [selectedHpr, setSelectedHpr] = useState(null);
  const [showHprModal, setShowHprModal] = useState(false);
  const [hprForm, setHprForm] = useState({
    vendor_name: '',
    invoice_number: '',
    purchase_cost: '',
    purchase_date: new Date().toISOString().split('T')[0],
    new_asset_label: '',
    warranty_details: '',
    finance_notes: ''
  });


  useEffect(() => {
    hostelInventoryAPI.getMyHostelProfile()
      .then(res => {
        if (res && res.allocated) {
          setHasHostelAllocation(true);
        }
      })
      .catch(() => {});
  }, []);
  const [loading, setLoading]     = useState(true);
  const [error,   setError]       = useState(null);
  const [success, setSuccess]     = useState('');

  /* ── stats ── */
  const [stats, setStats] = useState({
    total_revenue_collected: 0, total_pending_fees: 0,
    total_overdue_count: 0,     total_students: 0,
    total_staff: 0,             monthly_salary_expense: 0,
    transactions_today: 0,      amount_collected_today: 0,
  });
  const [labTotalRevenue, setLabTotalRevenue] = useState(0);
  const [labTodayRevenue, setLabTodayRevenue] = useState(0);

  /* ── payroll calculation state ── */
  const [calcFromDate,    setCalcFromDate]   = useState(DEFAULT_BOUNDS.from);
  const [calcToDate,      setCalcToDate]     = useState(DEFAULT_BOUNDS.to);
  const [selectedRoles,   setSelectedRoles]  = useState([]);   // [] = all roles
  const [calcResult,      setCalcResult]     = useState(null); // last API response

  /* ── payroll view state ── */
  const [payrollRecords,   setPayrollRecords]   = useState([]);
  const [payrollSummary,   setPayrollSummary]   = useState(null);
  const [selectedPayrolls, setSelectedPayrolls] = useState([]);
  const [showPayrollDetail,setShowPayrollDetail]= useState(false);
  const [selectedPayroll,  setSelectedPayroll]  = useState(null);
  const [submitting,       setSubmitting]       = useState(false);
  const [viewMonth,        setViewMonth]        = useState(NOW.getMonth() + 1);
  const [viewYear,         setViewYear]         = useState(NOW.getFullYear());
  const [payrollStatusFlt, setPayrollStatusFlt] = useState('');

  /* ── user list state ── */
  const [students,     setStudents]     = useState([]);
  const [facultyUsers, setFacultyUsers] = useState([]);
  const [otherStaff,   setOtherStaff]   = useState([]);
  const [paymentHist,  setPaymentHist]  = useState([]);
  const [labOrders,    setLabOrders]    = useState([]);
  const [labPayModal,  setLabPayModal]  = useState(null);
  const [labPayMethod, setLabPayMethod] = useState('Cash');
  const [searchQuery,  setSearchQuery]  = useState('');
  const [courseFilter, setCourseFilter] = useState('');
  const [overdueOnly,  setOverdueOnly]  = useState(false);

  /* ── modals ── */
  const [showDetails,  setShowDetails]  = useState(false);
  const [showEdit,     setShowEdit]     = useState(false);
  const [showPayment,  setShowPayment]  = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editForm,     setEditForm]     = useState({});
  const [payForm,      setPayForm]      = useState({
    amount: '', payment_method: 'Cash', transaction_type: 'TUITION_FEE', remarks: '',
  });

  /* ── derived ── */
  const calcDays = (calcFromDate && calcToDate)
    ? Math.floor((new Date(calcToDate) - new Date(calcFromDate)) / 86400000) + 1
    : 0;

  const isFullMonth = (() => {
    if (!calcFromDate) return true;
    const y = parseInt(calcFromDate.slice(0, 4));
    const m = parseInt(calcFromDate.slice(5, 7));
    const b = monthBounds(y, m);
    return calcFromDate === b.from && calcToDate === b.to;
  })();

  /* ─── mount ─────────────────────────────────────────────────────────────── */
  useEffect(() => { loadDashboardData(); }, []);

  useEffect(() => {
    if      (activeTab === 'students')    loadStudents();
    else if (activeTab === 'faculty')     loadFaculty();
    else if (activeTab === 'staff')       loadOtherStaff();
    else if (activeTab === 'payments')    loadPaymentHistory();
    else if (activeTab === 'payroll')     loadPayrollData();
    else if (activeTab === 'hostel-procurement') loadHostelPurchaseRequests();
    else if (activeTab === 'lab-billing') {
      labAPI.getOrders({ status: 'ORDERED,BILLED' })
        .then(d => setLabOrders(Array.isArray(d) ? d : d.results || []))
        .catch(console.error);
    }
  }, [activeTab, courseFilter, overdueOnly, viewMonth, viewYear, payrollStatusFlt]);

  /* ─── loaders ────────────────────────────────────────────────────────────── */
  const loadDashboardData = async () => {
    try {
      setLoading(true); setError(null);
      const [sR, lR] = await Promise.allSettled([
        financeAPI.getDashboardStats(), labAPI.getOrders({}),
      ]);
      if (sR.status === 'fulfilled') setStats(sR.value);
      if (lR.status === 'fulfilled') {
        const orders    = Array.isArray(lR.value) ? lR.value : (lR.value?.results || []);
        const paidSt    = ['PAID', 'SAMPLE_COLLECTED', 'IN_PROGRESS', 'COMPLETED'];
        const todayLocal = new Date().toLocaleDateString('en-CA');
        const toLocal    = s => s ? new Date(s).toLocaleDateString('en-CA') : null;
        setLabTotalRevenue(orders.filter(o => paidSt.includes(o.status))
          .reduce((s, o) => s + parseFloat(o.final_amount || o.bill_amount || 0), 0));
        setLabTodayRevenue(orders.filter(o => paidSt.includes(o.status))
          .filter(o => toLocal(o.payment_confirmed_at || o.order_date) === todayLocal)
          .reduce((s, o) => s + parseFloat(o.final_amount || o.bill_amount || 0), 0));
      }
    } catch (e) { setError('Failed to load dashboard.'); }
    finally     { setLoading(false); }
  };

  const loadPayrollData = async () => {
    try {
      setLoading(true); setError(null);
      const p = { month: viewMonth, year: viewYear };
      if (payrollStatusFlt) p.status = payrollStatusFlt;
      const recs = await payrollAPI.getPayrollRecords(p);
      setPayrollRecords(Array.isArray(recs) ? recs : recs.results || []);
      const sum = await payrollAPI.getPayrollSummary(viewMonth, viewYear);
      setPayrollSummary(sum);
    } catch (e) { setError(e.response?.data?.error || 'Failed to load payroll.'); }
    finally     { setLoading(false); }
  };



  const loadStudents = async () => {
    try {
      setLoading(true);
      const p = { role: 'STUDENT' };
      if (courseFilter) p.course = courseFilter;
      if (overdueOnly) p.overdue = 'true';
      const d = await financeAPI.getUsers(p);
      let sList = Array.isArray(d) ? d : (d.results || []);
      if (overdueOnly) {
        const todayStr = new Date().toISOString().slice(0, 10);
        sList = sList.filter(s => {
          const fr = s.financial_record || {};
          const pend = parseFloat(s.tuition_pending ?? fr.tuition_pending ?? 0);
          const deadline = s.payment_deadline || fr.payment_deadline || '';
          const isOverdueFlag = s.is_overdue || fr.is_overdue;
          if (pend <= 0) return false;
          if (isOverdueFlag) return true;
          if (deadline && deadline !== 'N/A' && deadline < todayStr) return true;
          return false;
        });
      }
      setStudents(sList);
    } catch (e) {
      setError('Failed to load students.');
    } finally {
      setLoading(false);
    }
  };
  const loadFaculty = async () => {
    try { setLoading(true); const d = await financeAPI.getUsers({ role: 'FACULTY' }); setFacultyUsers(Array.isArray(d) ? d : d.results || []); }
    catch (e) { setError('Failed to load faculty.'); } finally { setLoading(false); }
  };
  const loadOtherStaff = async () => {
    try {
      setLoading(true);
      const roles = ['FINANCE','HOSTEL_ADMIN','LIBRARY_STAFF','TRANSPORT_MANAGER','recepsonist',
        'LAB_TECH_MICROBIOLOGY','LAB_TECH_PATHOLOGY','LAB_TECH_BIOCHEMISTRY',
        'BILLING_MEMBER','BILLING_MANAGER','SAMPLE_COLLECTOR','REPORT_GENERATION',
        'GRIEVANCE_OFFICER','PRINCIPAL','DEAN'];
      const all = [];
      for (const r of roles) { const d = await financeAPI.getUsers({ role: r }); all.push(...(Array.isArray(d) ? d : d.results || [])); }
      setOtherStaff(all);
    } catch (e) { setError('Failed to load staff.'); } finally { setLoading(false); }
  };
  const loadPaymentHistory = async () => {
    try { setLoading(true); const d = await financeAPI.getPaymentHistory(); setPaymentHist(Array.isArray(d) ? d : d.results || []); }
    catch (e) { setError('Failed to load payments.'); } finally { setLoading(false); }
  };
  
  const loadHostelPurchaseRequests = async () => {
    try {
      setLoading(true);
      const d = await hostelInventoryAPI.getHostelPurchaseRequests();
      setHostelPurchaseRequests(Array.isArray(d) ? d : d.results || []);
    } catch (e) {
      setError('Failed to load hostel purchase requests.');
    } finally {
      setLoading(false);
    }
  };

  const handleProcessHprSubmit = async (e) => {
    e.preventDefault();
    if (!selectedHpr) return;
    try {
      setLoading(true);
      await hostelInventoryAPI.updateHostelPurchaseRequest(selectedHpr.id, {
        ...hprForm,
        status: 'PURCHASED',
      });
      setSuccess('Hostel purchase request processed as PURCHASED successfully.');
      setShowHprModal(false);
      loadHostelPurchaseRequests();
    } catch (err) {
      setError(err.response?.data?.error || JSON.stringify(err.response?.data) || 'Failed to process purchase request.');
    } finally {
      setLoading(false);
    }
  };

  /* ─── month preset ───────────────────────────────────────────────────────── */
  const applyMonthPreset = (year, month) => {
    const b = monthBounds(year, month);
    setCalcFromDate(b.from);
    setCalcToDate(b.to);
    setViewMonth(month);
    setViewYear(year);
  };

  /* ─── payroll calculation ────────────────────────────────────────────────── */
  const handleCalculatePayroll = async () => {
    if (!calcFromDate || !calcToDate) return notify.error('Please select From Date and To Date.');
    if (calcToDate < calcFromDate)    return notify.success('To Date must be after From Date.');

    const rolesLabel = selectedRoles.length === 0 ? 'All Roles' : selectedRoles.join(', ');
    const daysLabel  = isFullMonth
      ? `Full month — ${calcDays} days`
      : `⚡ Partial period — ${calcDays} days only (prorated salary)`;

    if (!await confirmDialog(
      `Calculate payroll?\n\nRoles: ${rolesLabel}\nPeriod: ${calcFromDate} → ${calcToDate}\n${daysLabel}\n\nProceed?`
    )) return;

    try {
      setLoading(true); setError(null); setCalcResult(null);
      const payload = { from_date: calcFromDate, to_date: calcToDate };
      if (selectedRoles.length > 0) payload.roles = selectedRoles;

      const res = await payrollAPI.calculatePayroll(payload);
      setCalcResult(res);

      const skip = res.already_exists_count || (res.already_exists || []).length || 0;
      const skipPre = res.skipped_pre_joining_count || (res.skipped_pre_joining || []).length || 0;
      const calc = res.calculated || 0;
      let msg = `✓ Payroll calculated for ${calc} record(s).`;
      if (skip > 0) msg += `\n⚠ ${skip} record(s) skipped — payroll already exists for that date range.`;
      if (skipPre > 0) msg += `\nℹ ${skipPre} employee(s) skipped — joined after payroll period (${calcToDate}).`;
      if ((res.errors || []).length > 0) msg += `\n✕ ${res.errors.length} error(s) occurred.`;
      notify.success(msg);

      await new Promise(r => setTimeout(r, 400));
      await loadPayrollData();
    } catch (e) {
      const msg = e.response?.data?.error || 'Payroll calculation failed.';
      setError(msg); notify.error(`Error: ${msg}`);
    } finally { setLoading(false); }
  };

  const handleApproveAndExport = async (ids) => {
    try {
      setSubmitting(true);
      await payrollAPI.approvePayroll(ids);
      const withBank = await Promise.all(ids.map(async id => {
        const rec = await payrollAPI.getPayrollDetail(id);
        try { const bank = await financeAPI.getUserBankDetail(rec.user); return { ...rec, user_bank_detail: bank }; }
        catch { return { ...rec, user_bank_detail: {} }; }
      }));
      generatePayrollCSV(withBank);
      setSuccess(`${ids.length} record(s) approved. CSV downloaded.`);
      setTimeout(() => setSuccess(''), 4000);
      loadPayrollData();
    } catch (e) { setError(e.response?.data?.error || 'Approve failed.'); }
    finally     { setSubmitting(false); }
  };

  const handleViewPayrollDetail = async (id) => {
    try { const d = await payrollAPI.getPayrollDetail(id); setSelectedPayroll(d); setShowPayrollDetail(true); }
    catch { notify.error('Failed to load details.'); }
  };

  const togglePayrollSel = id =>
    setSelectedPayrolls(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const toggleRole = val =>
    setSelectedRoles(p => p.includes(val) ? p.filter(r => r !== val) : [...p, val]);

  /* ─── edit / payment ─────────────────────────────────────────────────────── */
  const handleEditClick = u => {
    setSelectedUser(u);
    setEditForm(u.role === 'STUDENT'
      ? { tuition_fee: u.financial_record?.tuition_fee || '', payment_deadline: u.financial_record?.payment_deadline || '' }
      : { monthly_salary: u.financial_record?.monthly_salary || '' });
    setShowEdit(true);
  };
  const handleEditSubmit = async e => {
    e.preventDefault();
    try {
      await financeAPI.updateFinancialRecord(selectedUser.id, editForm);
      notify.success('Updated.'); setShowEdit(false);
      if (activeTab === 'students') loadStudents();
      else if (activeTab === 'faculty') loadFaculty();
      else if (activeTab === 'staff') loadOtherStaff();
      loadDashboardData();
    } catch (e) { notify.error(e.response?.data?.error || 'Update failed.'); }
  };
  const handleRecordPaymentClick = u => {
    setSelectedUser(u);
    const pendAmt = u.tuition_pending ?? u.financial_record?.tuition_pending ?? '';
    setPayForm({ amount: pendAmt, payment_method: 'Cash', transaction_type: 'TUITION_FEE', remarks: 'Tuition Fee Payment' });
    setShowPayment(true);
  };
  const handlePaymentSubmit = async e => {
    e.preventDefault();
    try {
      await financeAPI.recordPayment({ user_id: selectedUser.id, ...payForm });
      notify.success('Payment recorded.'); setShowPayment(false);
      if (activeTab === 'students') loadStudents();
      loadDashboardData();
    } catch (e) { notify.error(e.response?.data?.error || 'Payment failed.'); }
  };

  /* ─── formatters ─────────────────────────────────────────────────────────── */
  const fmtCur  = n => (!n && n !== 0) ? '₹0' : `₹${parseFloat(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtDate = s => s ? new Date(s).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A';
  const monthName = m => ['','Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][m];
  const payBadge  = s => `badge ${{ DRAFT:'badge-gray',CALCULATED:'badge-blue',APPROVED:'badge-green',PAID:'badge-green' }[s] || 'badge-gray'}`;
  const payStatus = u => { const r = u.financial_record; if (!r) return 'No Record'; if (r.is_overdue && r.tuition_pending > 0) return 'Overdue'; if (r.tuition_pending > 0) return 'Pending'; return 'Paid'; };
  const badgeCls  = (ov, pend) => ov && pend > 0 ? 'badge-red' : pend > 0 ? 'badge-yellow' : 'badge-green';
  const filterUsers = list => {
    if (!searchQuery) return list;
    const q = searchQuery.toLowerCase();
    return list.filter(u => [u.username, u.email, u.full_name, u.enrollment_id].some(f => f?.toLowerCase().includes(q)));
  };

  /* ─── render ─────────────────────────────────────────────────────────────── */
  if (loading && !stats.total_students && !payrollRecords.length) {
    return (
      <DashboardLayout title="Finance Dashboard">
        <div className="loading-container"><div className="spinner-large"/><p>Loading…</p></div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Finance Dashboard">
      <div className="dashboard-container">

        {error   && <div className="error-alert">{error}<button onClick={() => setError(null)} className="error-close">&times;</button></div>}
        {success && <div className="error-alert" style={{ background: '#f0fdf4', borderColor: '#bbf7d0', color: '#166534' }}>✓ {success}<button onClick={() => setSuccess('')} className="error-close">&times;</button></div>}

        

        {/* tabs */}
        <div className="tabs-container">
          <div className="tabs" style={{ flexWrap: 'wrap', gap: 4 }}>
            {[
              ['overview',          'Overview'],
              ['analytics',         '📊 Reports & Analytics'],
              ['fee-structures',    '🎓 Fee Structures'],
              ['payroll',           'Payroll'],
              ['salary-structures', '💰 Salary Structures'],
              ['students',          `Students (${stats.total_students})`],
              ['faculty',           'Faculty'],
              ['staff',             'Other Staff'],
              ['approve-payslips',  'Approve PaySlips'],
              ['payments',          'Payment History'],
              ['expenses',          '💸 Daily Expenses'],
              ['transport-expenses', '🚌 Transport Expenses'],
              ['calendar',          '📅 Calendar'],
              
              ...(user?.role === 'FINANCE'
                ? [['hostel-procurement', '🏨 Hostel Procurement']]
                : hasHostelAllocation
                ? [['hostel', '🏨 Hostel']]
                : []),
            ].map(([key, label]) => (
              <button key={key} className={`tab ${activeTab === key ? 'active' : ''}`} onClick={() => setActiveTab(key)}>{label}</button>
            ))}
          </div>
        </div>

        <div className="tab-content">

          {/* ── OVERVIEW ── */}
          {activeTab === 'analytics' && <FinanceReportsAnalyticsTab stats={stats} onPayStudent={handleRecordPaymentClick} />}

          {activeTab === 'overview' && (
            <div className="overview-grid">
              <div className="card">
                <div className="card-header"><h2>Financial Summary</h2></div>
                <div className="card-body">
                  <div className="summary-grid">
                    {[
                      ['Total Students',        stats.total_students,                        ''],
                      ['Total Staff',           stats.total_staff,                           ''],
                      ['Monthly Salary Expense',fmtCur(stats.monthly_salary_expense),        ''],
                      ['Revenue Collected',     fmtCur(stats.total_revenue_collected),       'highlight-green'],
                      ['Pending Fees',          fmtCur(stats.total_pending_fees),            'highlight-orange'],
                      ['Overdue Count',         stats.total_overdue_count,                   'highlight-red'],
                    ].map(([l, v, c]) => (
                      <div className="summary-item" key={l}>
                        <span className="summary-label">{l}:</span>
                        <span className={`summary-value ${c}`}>{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="card">
                <div className="card-header"><h2>Quick Actions</h2></div>
                <div className="card-body">
                  <div className="quick-actions-grid">
                    {[['payroll','Manage Payroll'],['salary-structures','Salary Structures'],['students','Manage Students'],['payments','View Payments']].map(([tab, label]) => (
                      <button key={tab} className="quick-action-btn" onClick={() => setActiveTab(tab)}><span>{label}</span></button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── FEE STRUCTURES ── */}
          {activeTab === 'fee-structures' && (
            <div className="card"><div className="card-body"><FeeStructures /></div></div>
          )}

          {/* ── SALARY STRUCTURES ── */}
          {activeTab === 'salary-structures' && (
            <div className="card"><div className="card-body"><SalaryStructures /></div></div>
          )}

          {/* ── PAYROLL ── */}
          {activeTab === 'payroll' && (
            <div className="payroll-management">

              {/* Calculation controls */}
              <div className="card">
                <div className="card-header" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 16 }}>

                  {/* Title + info row */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                    <div>
                      <h2 style={{ margin: 0 }}>Calculate Payroll</h2>
                      <p style={{ margin: '4px 0 0', fontSize: 13, color: isFullMonth ? '#6b7280' : '#d97706' }}>
                        {isFullMonth
                          ? `✓ Full month selected — salary paid for all ${calcDays} days`
                          : `⚡ Partial period: ${calcDays} day(s) only. Gross = (Monthly ÷ 30) × ${calcDays}`}
                      </p>
                    </div>
                    {/* Quick preset buttons */}
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {[-1, 0].map(offset => {
                        const d = new Date(NOW.getFullYear(), NOW.getMonth() + offset, 1);
                        const y = d.getFullYear(), m = d.getMonth() + 1;
                        return (
                          <button key={offset} onClick={() => applyMonthPreset(y, m)} style={{
                            padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                            background: '#f3f4f6', border: '1px solid #e5e7eb', color: '#374151',
                          }}>
                            {offset === 0 ? 'This Month' : 'Last Month'} ({monthName(m)} {y})
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Date range + action row */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end', width: '100%' }}>
                    <div>
                      <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>FROM DATE</label>
                      <input type="date" className="filter-select" value={calcFromDate}
                        onChange={e => setCalcFromDate(e.target.value)} style={{ minWidth: 150 }}/>
                    </div>
                    <div>
                      <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>TO DATE</label>
                      <input type="date" className="filter-select" value={calcToDate}
                        onChange={e => setCalcToDate(e.target.value)} style={{ minWidth: 150 }}/>
                    </div>
                    <div style={{ fontSize: 13, paddingBottom: 6 }}>
                      <strong style={{ color: '#1a1d2b' }}>{calcDays}</strong> day(s) &nbsp;
                      {!isFullMonth && calcDays > 0 && (
                        <span style={{ color: '#d97706', fontWeight: 600, fontSize: 12 }}>
                          (prorated — not full month)
                        </span>
                      )}
                    </div>
                    <button className="btn-primary" onClick={handleCalculatePayroll} disabled={loading} style={{ alignSelf: 'flex-end' }}>
                      ⚙ Calculate Payroll
                    </button>
                    {selectedPayrolls.length > 0 && (
                      <button className="btn-success" disabled={submitting}
                        onClick={() => handleApproveAndExport(selectedPayrolls)} style={{ alignSelf: 'flex-end' }}>
                        ✓ Approve & Export ({selectedPayrolls.length})
                      </button>
                    )}
                  </div>

                  {/* Role filter chips */}
                  <div style={{ width: '100%' }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 6 }}>
                      FILTER BY ROLE&nbsp;
                      <span style={{ fontWeight: 400, color: '#9ca3af' }}>
                        ({selectedRoles.length === 0 ? 'All roles selected' : `${selectedRoles.length} selected`})
                      </span>
                    </label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      <button onClick={() => setSelectedRoles([])} style={{
                        padding: '4px 10px', borderRadius: 16, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                        background: selectedRoles.length === 0 ? '#4f46e5' : '#f3f4f6',
                        color: selectedRoles.length === 0 ? '#fff' : '#374151', border: '1px solid #e5e7eb',
                      }}>All</button>
                      {ALL_PAYROLL_ROLES.map(({ value, label }) => {
                        const sel = selectedRoles.includes(value);
                        return (
                          <button key={value} onClick={() => toggleRole(value)} style={{
                            padding: '4px 10px', borderRadius: 16, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                            background: sel ? '#4f46e5' : '#f3f4f6',
                            color: sel ? '#fff' : '#374151', border: '1px solid #e5e7eb',
                          }}>{label}</button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* ── Calculation result banner ── */}
                {calcResult && (
                  <div style={{ padding: '12px 20px', borderTop: '1px solid #e5e7eb' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 8 }}>
                      <span style={{ background: '#dcfce7', color: '#166534', padding: '4px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600 }}>
                        ✓ {calcResult.calculated} calculated
                      </span>
                      {(calcResult.already_exists_count || (calcResult.already_exists || []).length) > 0 && (
                        <span style={{ background: '#fef9c3', color: '#854d0e', padding: '4px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600 }}>
                          ⚠ {calcResult.already_exists_count || (calcResult.already_exists || []).length} skipped (duplicate period)
                        </span>
                      )}
                      {(calcResult.skipped_pre_joining_count || (calcResult.skipped_pre_joining || []).length) > 0 && (
                        <span style={{ background: '#e0f2fe', color: '#0369a1', padding: '4px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600 }}>
                          ℹ {calcResult.skipped_pre_joining_count || (calcResult.skipped_pre_joining || []).length} skipped (joined after period)
                        </span>
                      )}
                      {(calcResult.errors || []).length > 0 && (
                        <span style={{ background: '#fee2e2', color: '#991b1b', padding: '4px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600 }}>
                          ✕ {calcResult.errors.length} error(s)
                        </span>
                      )}
                    </div>

                    {/* Skipped duplicate records expandable list */}
                    {(calcResult.already_exists || []).length > 0 && (
                      <details style={{ marginTop: 4 }}>
                        <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#854d0e', userSelect: 'none' }}>
                          View skipped employees with existing payroll ({calcResult.already_exists.length})
                        </summary>
                        <div style={{ marginTop: 8, maxHeight: 180, overflowY: 'auto', border: '1px solid #fde68a', borderRadius: 8, background: '#fffbeb' }}>
                          {calcResult.already_exists.map((e, i) => (
                            <div key={i} style={{ padding: '8px 12px', borderBottom: '1px solid #fde68a', fontSize: 12, color: '#374151', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 4 }}>
                              <span>
                                <strong>{e.user_name}</strong>
                                {e.user_employee_id && <span style={{ color: '#9ca3af', marginLeft: 6 }}>({e.user_employee_id})</span>}
                              </span>
                              <span style={{ color: '#854d0e', fontWeight: 600 }}>
                                Existing: {e.existing_from_date} → {e.existing_to_date}
                                <span style={{ marginLeft: 6, background: '#fde68a', padding: '1px 6px', borderRadius: 10 }}>
                                  {e.existing_working_days} days
                                </span>
                              </span>
                            </div>
                          ))}
                        </div>
                      </details>
                    )}

                    {/* Skipped pre-joining records expandable list */}
                    {(calcResult.skipped_pre_joining || []).length > 0 && (
                      <details style={{ marginTop: 6 }}>
                        <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#0369a1', userSelect: 'none' }}>
                          View employees not yet joined in period ({calcResult.skipped_pre_joining.length})
                        </summary>
                        <div style={{ marginTop: 8, maxHeight: 180, overflowY: 'auto', border: '1px solid #bae6fd', borderRadius: 8, background: '#f0f9ff' }}>
                          {calcResult.skipped_pre_joining.map((e, i) => (
                            <div key={i} style={{ padding: '8px 12px', borderBottom: '1px solid #bae6fd', fontSize: 12, color: '#374151', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 4 }}>
                              <span>
                                <strong>{e.user_name}</strong>
                                {e.user_employee_id && <span style={{ color: '#9ca3af', marginLeft: 6 }}>({e.user_employee_id})</span>}
                              </span>
                              <span style={{ color: '#0369a1', fontWeight: 600 }}>
                                Joined: {e.joining_date}
                                <span style={{ marginLeft: 6, background: '#bae6fd', padding: '1px 6px', borderRadius: 10, fontSize: 11 }}>
                                  Joined after cycle
                                </span>
                              </span>
                            </div>
                          ))}
                        </div>
                      </details>
                    )}

                    {/* Errors expandable list */}
                    {(calcResult.errors || []).length > 0 && (
                      <details style={{ marginTop: 8 }}>
                        <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#991b1b', userSelect: 'none' }}>
                          View errors ({calcResult.errors.length})
                        </summary>
                        <div style={{ marginTop: 8, maxHeight: 140, overflowY: 'auto', border: '1px solid #fecaca', borderRadius: 8, background: '#fef2f2' }}>
                          {calcResult.errors.map((e, i) => (
                            <div key={i} style={{ padding: '6px 12px', borderBottom: '1px solid #fecaca', fontSize: 12, color: '#374151' }}>
                              <strong>{e.user_name || `User #${e.user_id}`}</strong>
                              <span style={{ color: '#b91c1c', marginLeft: 8 }}>{e.error}</span>
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                  </div>
                )}

                {/* ── Payroll summary cards ── */}
                {payrollSummary && (
                  <div className="card-body">
                    <div className="payroll-summary-grid">
                      {[
                        ['Total Employees',  payrollSummary.total_employees,              ''],
                        ['Gross Salary',     fmtCur(payrollSummary.total_gross_salary),   ''],
                        ['Total Deductions', fmtCur(payrollSummary.total_deductions),     'highlight-red'],
                        ['Net Payable',      fmtCur(payrollSummary.total_net_salary),     'highlight-green'],
                        ['Unpaid Leave Days',payrollSummary.total_unpaid_leaves,          'highlight-orange'],
                        ['Pending Approval', payrollSummary.pending_approval,             ''],
                      ].map(([l, v, c]) => (
                        <div className="summary-item" key={l}>
                          <span className="summary-label">{l}:</span>
                          <span className={`summary-value ${c}`}>{v}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* ── Records table ── */}
              <div className="card">
                <div className="card-header">
                  <h2>Records — {monthName(viewMonth)} {viewYear}</h2>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <select className="filter-select" value={viewMonth} onChange={e => setViewMonth(+e.target.value)}>
                      {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => <option key={m} value={m}>{monthName(m)}</option>)}
                    </select>
                    <select className="filter-select" value={viewYear} onChange={e => setViewYear(+e.target.value)}>
                      {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                    <select className="filter-select" value={payrollStatusFlt} onChange={e => setPayrollStatusFlt(e.target.value)}>
                      <option value="">All Status</option>
                      {['CALCULATED','APPROVED','PAID'].map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
                <div className="card-body">
                  <div className="table-container">
                    <table className="data-table payroll-table">
                      <thead>
                        <tr>
                          <th>
                            <input type="checkbox"
                              onChange={e => setSelectedPayrolls(e.target.checked
                                ? payrollRecords.filter(p => p.status === 'CALCULATED').map(p => p.id) : [])}
                              checked={selectedPayrolls.length > 0 &&
                                selectedPayrolls.length === payrollRecords.filter(p => p.status === 'CALCULATED').length}/>
                          </th>
                          <th>Employee</th>
                          <th>Role</th>
                          <th>Period</th>
                          <th>Days</th>
                          <th>Gross</th>
                          <th>LOP</th>
                          <th>Deductions</th>
                          <th>Net Salary</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payrollRecords.length === 0 ? (
                          <tr><td colSpan="11" className="text-center">
                            <div className="empty-state">
                              <p>No records for {monthName(viewMonth)} {viewYear}.</p>
                              <p>Select a date range above and click "Calculate Payroll".</p>
                            </div>
                          </td></tr>
                        ) : payrollRecords.map(p => (
                          <tr key={p.id}>
                            <td>
                              {p.status === 'CALCULATED' && (
                                <input type="checkbox" checked={selectedPayrolls.includes(p.id)} onChange={() => togglePayrollSel(p.id)}/>
                              )}
                            </td>
                            <td>
                              <strong>{p.user_name}</strong>
                              <br/><small style={{ color: '#9ca3af' }}>{p.user_employee_id}</small>
                              {p.user_joining_date && (
                                <div style={{ fontSize: 11, color: '#2563eb', marginTop: 2, fontWeight: 600 }}>
                                  🗓️ Joined: {p.user_joining_date}
                                </div>
                              )}
                            </td>
                            <td><span className="badge badge-blue">{p.user_subrole || p.user_role}</span></td>
                            <td style={{ fontSize: 12, color: '#374151' }}>
                              {p.from_date && p.to_date ? (
                                <>
                                  <div style={{ fontWeight: 600 }}>{p.from_date}</div>
                                  <div style={{ color: '#9ca3af' }}>→ {p.to_date}</div>
                                  {p.user_joining_date && p.from_date === p.user_joining_date && (
                                    <span style={{ fontSize: 10, background: '#eff6ff', color: '#1d4ed8', padding: '1px 5px', borderRadius: 4, display: 'inline-block', marginTop: 2, fontWeight: 700 }}>
                                      Prorated from Joining
                                    </span>
                                  )}
                                </>
                              ) : `${monthName(p.month)} ${p.year}`}
                            </td>
                            <td className="text-center">
                              <span className={`badge ${ (p.working_days || 30) < 28 ? 'badge-orange' : 'badge-gray' }`}
                                title={(p.working_days || 30) < 28 ? 'Partial period' : 'Full month'}>
                                {p.working_days || 30}d
                              </span>
                            </td>
                            <td className="text-right">{fmtCur(p.gross_salary)}</td>
                            <td className="text-center">
                              {p.unpaid_leave_days > 0
                                ? <span className="badge badge-orange">{p.unpaid_leave_days}</span>
                                : <span className="text-muted">—</span>}
                            </td>
                            <td className="text-right">
                              {parseFloat(p.total_deductions) > 0
                                ? <span className="text-red">{fmtCur(p.total_deductions)}</span>
                                : <span className="text-muted">₹0.00</span>}
                            </td>
                            <td className="text-right">
                              <strong className="text-green">{fmtCur(p.net_salary)}</strong>
                            </td>
                            <td><span className={payBadge(p.status)}>{p.status_display}</span></td>
                            <td>
                              <button className="btn-sm btn-outline" onClick={() => handleViewPayrollDetail(p.id)}>Details</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* ── PAYROLL DETAIL MODAL ── */}
              {showPayrollDetail && selectedPayroll && (
                <div
                  onClick={() => setShowPayrollDetail(false)}
                  style={{
                    position: 'fixed',
                    inset: 0,
                    background: 'rgba(15,23,42,0.6)',
                    zIndex: 1100,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 16,
                  }}
                >
                  <div
                    onClick={e => e.stopPropagation()}
                    style={{
                      background: '#ffffff',
                      borderRadius: 14,
                      width: '100%',
                      maxWidth: 580,
                      maxHeight: '90vh',
                      overflowY: 'auto',
                      padding: 24,
                      boxShadow: '0 20px 40px rgba(0,0,0,0.25)',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '1px solid #e2e8f0', paddingBottom: 12, marginBottom: 16 }}>
                      <div>
                        <h3 style={{ fontSize: 18, fontWeight: 800, color: '#0f172a', margin: 0 }}>
                          📄 Payroll Calculation Details
                        </h3>
                        <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
                          {selectedPayroll.user_name} ({selectedPayroll.user_employee_id || 'ID N/A'}) • {selectedPayroll.user_subrole || selectedPayroll.user_role}
                        </div>
                      </div>
                      <button
                        onClick={() => setShowPayrollDetail(false)}
                        style={{ background: 'none', border: 'none', fontSize: 22, color: '#94a3b8', cursor: 'pointer', lineHeight: 1 }}
                      >
                        ✕
                      </button>
                    </div>

                    {/* Joining Date & Period Card */}
                    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, fontSize: 13 }}>
                        <div>
                          <span style={{ color: '#64748b', display: 'block', fontSize: 11, fontWeight: 700 }}>DATE OF JOINING</span>
                          <strong style={{ color: '#1d4ed8' }}>
                            {selectedPayroll.user_joining_date || 'Standard'}
                          </strong>
                        </div>
                        <div>
                          <span style={{ color: '#64748b', display: 'block', fontSize: 11, fontWeight: 700 }}>PAYROLL STATUS</span>
                          <span className={payBadge(selectedPayroll.status)}>{selectedPayroll.status_display}</span>
                        </div>
                        <div>
                          <span style={{ color: '#64748b', display: 'block', fontSize: 11, fontWeight: 700 }}>PAYROLL PERIOD</span>
                          <strong>{selectedPayroll.from_date || '—'} → {selectedPayroll.to_date || '—'}</strong>
                        </div>
                        <div>
                          <span style={{ color: '#64748b', display: 'block', fontSize: 11, fontWeight: 700 }}>WORKING DAYS IN PERIOD</span>
                          <strong style={{ color: '#0f172a' }}>{selectedPayroll.working_days} days</strong>
                          {selectedPayroll.user_joining_date && selectedPayroll.from_date === selectedPayroll.user_joining_date && (
                            <span style={{ marginLeft: 6, fontSize: 10, background: '#eff6ff', color: '#1d4ed8', padding: '1px 5px', borderRadius: 4, fontWeight: 700 }}>
                              Prorated from Joining
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Salary Breakdown */}
                    <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 10 }}>Salary Breakdown</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: '#64748b' }}>Base Monthly Salary:</span>
                          <strong>{fmtCur(selectedPayroll.base_salary)}</strong>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: '#64748b' }}>Daily Salary Rate:</span>
                          <span>{fmtCur(selectedPayroll.daily_salary)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #f1f5f9', paddingTop: 6 }}>
                          <span style={{ color: '#0f172a', fontWeight: 600 }}>Prorated Gross Salary:</span>
                          <strong style={{ color: '#0f172a' }}>{fmtCur(selectedPayroll.gross_salary)}</strong>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: '#dc2626' }}>Leave Deduction (Unpaid / LOP):</span>
                          <span style={{ color: '#dc2626' }}>- {fmtCur(selectedPayroll.leave_deduction)}</span>
                        </div>
                        {parseFloat(selectedPayroll.late_deduction || 0) > 0 && (
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: '#d97706' }}>Late Penalty ({selectedPayroll.late_days} late days, {selectedPayroll.late_penalty_days} penalty days):</span>
                            <span style={{ color: '#d97706' }}>- {fmtCur(selectedPayroll.late_deduction)}</span>
                          </div>
                        )}
                        {parseFloat(selectedPayroll.other_deductions || 0) > 0 && (
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: '#64748b' }}>Other Deductions:</span>
                            <span>- {fmtCur(selectedPayroll.other_deductions)}</span>
                          </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '2px solid #e2e8f0', paddingTop: 8, fontSize: 15, fontWeight: 800 }}>
                          <span style={{ color: '#166534' }}>Net Payable Salary:</span>
                          <span style={{ color: '#166534' }}>{fmtCur(selectedPayroll.net_salary)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Leave Details Breakdown if any */}
                    {(selectedPayroll.leave_details || []).length > 0 && (
                      <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 8 }}>Approved Leaves in Period</div>
                        <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ background: '#f8fafc', color: '#64748b', textAlign: 'left' }}>
                              <th style={{ padding: '6px 8px' }}>Leave Type</th>
                              <th style={{ padding: '6px 8px' }}>Period</th>
                              <th style={{ padding: '6px 8px' }}>Days</th>
                              <th style={{ padding: '6px 8px' }}>Paid?</th>
                              <th style={{ padding: '6px 8px', textAlign: 'right' }}>Deduction</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedPayroll.leave_details.map((l, i) => (
                              <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                <td style={{ padding: '6px 8px' }}>{l.leave_type_display || l.leave_type}</td>
                                <td style={{ padding: '6px 8px' }}>{l.from_date} → {l.to_date}</td>
                                <td style={{ padding: '6px 8px' }}>{l.days_in_month}d</td>
                                <td style={{ padding: '6px 8px' }}>{l.is_paid ? '✓ Paid' : '✕ Unpaid'}</td>
                                <td style={{ padding: '6px 8px', textAlign: 'right', color: l.deduction_amount > 0 ? '#dc2626' : '#64748b' }}>
                                  {fmtCur(l.deduction_amount)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <button
                        type="button"
                        onClick={() => setShowPayrollDetail(false)}
                        style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#ffffff', color: '#334155', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── STUDENTS ── */}
          {activeTab === 'students' && (
            <div className="card">
              <div className="card-header"><h2>Student Financial Records</h2></div>
              <div className="card-body">
                <div className="table-filters">
                  <input type="search" placeholder="Search…" className="search-input" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}/>
                  <select className="filter-select" value={courseFilter} onChange={e => setCourseFilter(e.target.value)}>
                    <option value="">All Courses</option>
                    {[['MBBS','MBBS'],['PG','Post Graduate'],['SUPER_SPECIALITY','Super Speciality'],['INTERNSHIP','Internship']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                  <label className="checkbox-label"><input type="checkbox" checked={overdueOnly} onChange={e => setOverdueOnly(e.target.checked)}/><span>Overdue Only</span></label>
                </div>
                <div className="table-container">
                  <table className="data-table">
                    <thead><tr><th>Student ID</th><th>Name</th><th>Course</th><th>Fee</th><th>Paid</th><th>Pending</th><th>Deadline</th><th>Status</th><th>Actions</th></tr></thead>
                    <tbody>
                      {filterUsers(students).map(s => {
                        const hasBus = s.has_active_transport || !!s.transport_assignment;
                        const busFee = s.bus_fee || s.transport_assignment?.bus_fee || 0;
                        const displayFee = s.tuition_fee ?? s.financial_record?.tuition_fee ?? 0;
                        const displayPaid = s.tuition_paid ?? s.financial_record?.tuition_paid ?? 0;
                        const displayPending = s.tuition_pending ?? Math.max(0, displayFee - displayPaid);

                        return (
                          <tr key={s.id}>
                            <td><strong>{s.enrollment_id}</strong></td>
                            <td>
                              <strong>{s.full_name}</strong>
                              {hasBus && (
                                <div style={{ fontSize: 10, color: '#1d4ed8', fontWeight: 700, marginTop: 2 }}>
                                  🚌 Bus: {s.transport_assignment?.route_number || 'Enrolled'} (+₹{busFee.toLocaleString('en-IN')})
                                </div>
                              )}
                            </td>
                            <td>{s.course_display}</td>
                            <td>{fmtCur(displayFee)}</td>
                            <td className="text-green">{fmtCur(displayPaid)}</td>
                            <td className="text-orange">{fmtCur(displayPending)}</td>
                            <td>{fmtDate(s.financial_record?.payment_deadline || s.payment_deadline)}</td>
                            <td><span className={`badge ${badgeCls(s.financial_record?.is_overdue || s.is_overdue, displayPending)}`}>{payStatus(s)}</span></td>
                            <td>
                              <div className="action-buttons">
                                <button className="btn-sm btn-outline" onClick={() => { setSelectedUser(s); setShowDetails(true); }}>View</button>
                                <button className="btn-sm btn-primary" onClick={() => handleEditClick(s)}>Edit</button>
                                {displayPending > 0 && <button className="btn-sm btn-success" onClick={() => handleRecordPaymentClick(s)}>Pay</button>}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {filterUsers(students).length === 0 && <tr><td colSpan="9" className="text-center">No students found</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ── FACULTY ── */}
          {activeTab === 'faculty' && (
            <div className="card">
              <div className="card-header"><h2>Faculty Salary Records</h2></div>
              <div className="card-body">
                <div className="table-filters"><input type="search" placeholder="Search…" className="search-input" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}/></div>
                <div className="table-container">
                  <table className="data-table">
                    <thead><tr><th>Employee ID</th><th>Name</th><th>Designation</th><th>Department</th><th>Monthly Salary</th><th>Last Paid</th><th>Actions</th></tr></thead>
                    <tbody>
                      {filterUsers(facultyUsers).map(f => (
                        <tr key={f.id}>
                          <td><strong>{f.enrollment_id}</strong></td><td><strong>{f.full_name}</strong></td>
                          <td>{f.course_display}</td><td>{f.profile?.faculty_department_name || 'N/A'}</td>
                          <td className="text-green">{fmtCur(f.financial_record?.monthly_salary)}</td>
                          <td>{fmtDate(f.financial_record?.last_salary_paid)}</td>
                          <td><div className="action-buttons">
                            <button className="btn-sm btn-outline" onClick={() => { setSelectedUser(f); setShowDetails(true); }}>View</button>
                            <button className="btn-sm btn-primary" onClick={() => handleEditClick(f)}>Edit</button>
                          </div></td>
                        </tr>
                      ))}
                      {filterUsers(facultyUsers).length === 0 && <tr><td colSpan="7" className="text-center">No faculty found</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ── OTHER STAFF ── */}
          {activeTab === 'staff' && (
            <div className="card">
              <div className="card-header"><h2>Other Staff Salary Records</h2></div>
              <div className="card-body">
                <div className="table-filters"><input type="search" placeholder="Search…" className="search-input" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}/></div>
                <div className="table-container">
                  <table className="data-table">
                    <thead><tr><th>Employee ID</th><th>Name</th><th>Role</th><th>Monthly Salary</th><th>Last Paid</th><th>Actions</th></tr></thead>
                    <tbody>
                      {filterUsers(otherStaff).map(s => (
                        <tr key={s.id}>
                          <td><strong>{s.enrollment_id}</strong></td><td><strong>{s.full_name}</strong></td>
                          <td>{s.role_display}</td>
                          <td className="text-green">{fmtCur(s.financial_record?.monthly_salary)}</td>
                          <td>{fmtDate(s.financial_record?.last_salary_paid)}</td>
                          <td><div className="action-buttons">
                            <button className="btn-sm btn-outline" onClick={() => { setSelectedUser(s); setShowDetails(true); }}>View</button>
                            <button className="btn-sm btn-primary" onClick={() => handleEditClick(s)}>Edit</button>
                          </div></td>
                        </tr>
                      ))}
                      {filterUsers(otherStaff).length === 0 && <tr><td colSpan="6" className="text-center">No staff found</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'approve-payslips' && (
            <div className="card" style={{ padding:0, overflow:'visible' }}>
              <ApprovePaySlip />
            </div>
        )}

          {/* ── PAYMENTS ── */}
          {activeTab === 'payments' && (
            <div className="card">
              <div className="card-header"><h2>Student Fee Payment Transactions</h2></div>
              <div className="card-body">
                <div className="table-filters"><input type="search" placeholder="Search…" className="search-input" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}/></div>
                <div className="table-container">
                  <table className="data-table">
                    <thead><tr><th>Tx ID</th><th>Student</th><th>Amount</th><th>Type</th><th>Method</th><th>Status</th><th>Date</th><th>Recorded By</th></tr></thead>
                    <tbody>
                      {paymentHist.length === 0 && <tr><td colSpan="8" className="text-center">No payment transactions recorded</td></tr>}
                      {paymentHist.map(t => (
                        <tr key={t.id}>
                          <td><strong>{t.transaction_id}</strong></td>
                          <td><strong>{t.user_name}</strong></td>
                          <td className="text-green"><strong>{fmtCur(t.amount)}</strong></td>
                          <td><span className="badge badge-blue">{t.transaction_type_display}</span></td>
                          <td>{t.payment_method || 'N/A'}</td>
                          <td><span className={`badge badge-${t.status === 'COMPLETED' ? 'green' : 'gray'}`}>{t.status_display}</span></td>
                          <td>{fmtDate(t.created_at)}</td>
                          <td>{t.recorded_by_name || 'System'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'hostel' && (
            <div className="card">
              <div className="card-body">
                <HostelUserPortalTab />
              </div>
            </div>
          )}

          {activeTab === 'hostel-procurement' && (
            <div className="card">
              <div className="card-header">
                <h2>Hostel Accessory Procurement Request Log</h2>
              </div>
              <div className="card-body">
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Request ID</th>
                        <th>Hostel & Room</th>
                        <th>Accessory Type</th>
                        <th>Qty</th>
                        <th>Justification & Priority</th>
                        <th>Status</th>
                        <th>Vendor / Cost Detail</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {hostelPurchaseRequests.length === 0 ? (
                        <tr>
                          <td colSpan="8" style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                            No hostel accessory procurement requests found.
                          </td>
                        </tr>
                      ) : (
                        hostelPurchaseRequests.map(hpr => (
                          <tr key={hpr.id}>
                            <td><strong>HPR-{hpr.id}</strong>{hpr.complaint && <span style={{ fontSize: '10px', color: '#6b7280' }}><br/>Complaint #{hpr.complaint}</span>}</td>
                            <td><strong>{hpr.room_number ? `Room ${hpr.room_number}` : 'Bulk Stock'}</strong><br/><span style={{ fontSize: '11px', color: '#6b7280' }}>{hpr.hostel_name}</span></td>
                            <td>
                              <span style={{ fontWeight: 'bold', color: '#4f46e5' }}>{hpr.accessory_type_display}</span>
                              {hpr.existing_asset_label && <div style={{ fontSize: '10px', color: '#dc2626' }}>Replacing: {hpr.existing_asset_label}</div>}
                            </td>
                            <td>{hpr.quantity}</td>
                            <td>
                              <div style={{ maxWidth: '250px', whiteSpace: 'normal', fontSize: '12px' }}>
                                <p style={{ margin: 0, fontStyle: 'italic' }}>"{hpr.reason}"</p>
                                <span className={`badge ${hpr.priority === 'HIGH' ? 'badge-red' : hpr.priority === 'MEDIUM' ? 'badge-orange' : 'badge-blue'}`} style={{ marginTop: '4px' }}>
                                  {hpr.priority_display}
                                </span>
                              </div>
                            </td>
                            <td>
                              <span className="badge" style={{
                                background: (hpr.status === 'REQUESTED' || hpr.status === 'PENDING') ? '#f59e0b' :
                                            hpr.status === 'APPROVED_BY_ADMIN' ? '#3b82f6' :
                                            hpr.status === 'PURCHASED' ? '#10b981' :
                                            hpr.status === 'INSTALLED' ? '#047857' : '#ef4444',
                                color: '#fff',
                                fontWeight: 'bold',
                                textTransform: 'uppercase'
                              }}>
                                {hpr.status_display}
                              </span>
                            </td>
                            <td>
                              {hpr.status === 'PURCHASED' || hpr.status === 'INSTALLED' ? (
                                <div style={{ fontSize: '12px' }}>
                                  <strong>₹{hpr.purchase_cost}</strong> via {hpr.vendor_name}
                                  <br/>
                                  <span style={{ fontSize: '10px', color: '#6b7280' }}>Invoice: {hpr.invoice_number}</span>
                                  {hpr.new_asset_label && <div style={{ fontSize: '10px', fontWeight: 'bold', color: '#16a34a' }}>New Label: {hpr.new_asset_label}</div>}
                                </div>
                              ) : (
                                <span style={{ fontSize: '11px', color: '#9ca3af', fontStyle: 'italic' }}>Awaiting Purchase</span>
                              )}
                            </td>
                            <td>
                              {hpr.status === 'APPROVED_BY_ADMIN' && (
                                <button className="btn-sm btn-success" onClick={() => {
                                  setSelectedHpr(hpr);
                                  setHprForm({
                                    vendor_name: '',
                                    invoice_number: '',
                                    purchase_cost: '',
                                    purchase_date: new Date().toISOString().split('T')[0],
                                    new_asset_label: hpr.new_asset_label || `${hpr.room_number || 'BULK'}-${hpr.accessory_type}-01`,
                                    warranty_details: '',
                                    finance_notes: ''
                                  });
                                  setShowHprModal(true);
                                }}>
                                  💳 Purchase Item
                                </button>
                              )}
                              {(hpr.status === 'REQUESTED' || hpr.status === 'PENDING') && (
                                <span style={{ fontSize: '11px', color: '#f59e0b', fontStyle: 'italic' }}>
                                  Awaiting Admin Approval
                                </span>
                              )}
                              {hpr.status === 'PURCHASED' && (
                                <span style={{ fontSize: '11px', color: '#10b981', fontStyle: 'italic' }}>
                                  Purchased, Awaiting Installation
                                </span>
                              )}
                              {hpr.status === 'INSTALLED' && (
                                <span style={{ fontSize: '11px', color: '#047857', fontWeight: 'bold' }}>
                                  Installed & Active
                                </span>
                              )}
                              {hpr.status === 'REJECTED' && (
                                <span style={{ fontSize: '11px', color: '#ef4444', fontStyle: 'italic' }}>
                                  Admin Rejected
                                </span>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

        </div>{/* end tab-content */}

        {activeTab === 'expenses'          && <DailyExpensesTab/>}
        {activeTab === 'transport-expenses'&& <TransportFinanceExpensesTab user={user} />}
        {activeTab === 'calendar'          && <div className="card" style={{ padding: 0, overflow: 'visible' }}><Calendar /></div>}
        

        {/* ── LAB BILLING ── */}
        {activeTab === 'lab-billing' && (
          <div className="card">
            <div className="card-header">
              <h2>Lab Test Billing</h2>
            </div>
            <div className="card-body">
              <div className="table-container">
                <table className="data-table">
                  <thead><tr><th>Order #</th><th>Patient</th><th>Token</th><th>Tests</th><th>Amount</th><th>Urgency</th><th>Status</th><th>Actions</th></tr></thead>
                  <tbody>
                    {labOrders.length === 0 && <tr><td colSpan="8" className="text-center">No pending lab orders</td></tr>}
                    {labOrders.map(o => (
                      <tr key={o.id}>
                        <td><strong>#{o.id}</strong></td>
                        <td><strong>{o.patient_name}</strong><br/><small>{o.patient_id}</small></td>
                        <td>{o.token_number || '—'}</td>
                        <td style={{ fontSize: 12, maxWidth: 200 }}>{o.items?.map(i => i.test_name).join(', ')}</td>
                        <td><strong>{fmtCur(o.bill_amount)}</strong></td>
                        <td><span className={`badge ${o.urgency === 'STAT' ? 'badge-red' : o.urgency === 'URGENT' ? 'badge-orange' : 'badge-gray'}`}>{o.urgency}</span></td>
                        <td><span className={`badge ${o.status === 'BILLED' ? 'badge-blue' : o.status === 'PAID' ? 'badge-green' : 'badge-gray'}`}>{o.status}</span></td>
                        <td><div className="action-buttons">
                          {!o.bill_generated && <button className="btn-sm btn-primary" onClick={async () => { try { const u = await labAPI.generateBill(o.id); setLabOrders(p => p.map(x => x.id === o.id ? u : x)); } catch (e) { notify.error(e.response?.data?.error || 'Failed'); } }}>Generate Bill</button>}
                          {o.bill_generated && !o.payment_confirmed && <button className="btn-sm btn-success" onClick={() => setLabPayModal(o)}>Confirm Payment</button>}
                          {o.payment_confirmed && <span className="text-green" style={{ fontSize: 12, fontWeight: 700 }}>✓ Paid</span>}
                        </div></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* MODALS */}
        {labPayModal && (
          <div className="modal-overlay" onClick={() => setLabPayModal(null)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <div className="modal-header"><h3>Confirm Lab Payment — #{labPayModal.id}</h3><button className="modal-close" onClick={() => setLabPayModal(null)}>&times;</button></div>
              <div className="modal-body">
                <p>Patient: <strong>{labPayModal.patient_name}</strong><br/>Amount: <strong>{fmtCur(labPayModal.bill_amount)}</strong></p>
                <div className="form-group"><label>Payment Method:</label>
                  <select className="form-input" value={labPayMethod} onChange={e => setLabPayMethod(e.target.value)}>
                    {['Cash','Card','UPI','Net Banking','Cheque'].map(m => <option key={m}>{m}</option>)}
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn-secondary" onClick={() => setLabPayModal(null)}>Cancel</button>
                <button className="btn-success" onClick={async () => { try { await labAPI.confirmPayment(labPayModal.id, { payment_method: labPayMethod }); setLabOrders(p => p.filter(o => o.id !== labPayModal.id)); setLabPayModal(null); } catch (e) { notify.error(e.response?.data?.error || 'Failed'); } }}>✓ Confirm</button>
              </div>
            </div>
          </div>
        )}

        {showEdit && selectedUser && (
          <div className="modal-overlay" onClick={() => setShowEdit(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <div className="modal-header"><h3>Edit Financial Record</h3><button className="modal-close" onClick={() => setShowEdit(false)}>&times;</button></div>
              <form onSubmit={handleEditSubmit}>
                <div className="modal-body">
                  <div className="form-group"><label>User:</label><input type="text" value={selectedUser.full_name} disabled className="form-input"/></div>
                  {selectedUser.role === 'STUDENT' && (<>
                    <div className="form-group"><label>Tuition Fee (₹):</label><input type="number" step="0.01" value={editForm.tuition_fee} onChange={e => setEditForm({ ...editForm, tuition_fee: e.target.value })} className="form-input" required/></div>
                    <div className="form-group"><label>Payment Deadline:</label><input type="date" value={editForm.payment_deadline} onChange={e => setEditForm({ ...editForm, payment_deadline: e.target.value })} className="form-input"/></div>
                  </>)}
                  {selectedUser.role !== 'STUDENT' && (
                    <div className="form-group"><label>Monthly Salary (₹):</label><input type="number" step="0.01" value={editForm.monthly_salary} onChange={e => setEditForm({ ...editForm, monthly_salary: e.target.value })} className="form-input" required/></div>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn-secondary" onClick={() => setShowEdit(false)}>Cancel</button>
                  <button type="submit" className="btn-primary">Update</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showPayment && selectedUser && (
          <div className="modal-overlay" onClick={() => setShowPayment(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <div className="modal-header"><h3>Record Payment</h3><button className="modal-close" onClick={() => setShowPayment(false)}>&times;</button></div>
              <form onSubmit={handlePaymentSubmit}>
                <div className="modal-body">
                  <div className="form-group"><label>Student:</label><input type="text" value={selectedUser.full_name} disabled className="form-input"/></div>
                  <div className="form-group"><label>Pending:</label><input type="text" value={fmtCur(selectedUser.tuition_pending ?? selectedUser.financial_record?.tuition_pending)} disabled className="form-input"/></div>
                  <div className="form-group"><label>Amount (₹):</label><input type="number" step="0.01" value={payForm.amount} onChange={e => setPayForm({ ...payForm, amount: e.target.value })} className="form-input" required/></div>
                  <div className="form-group"><label>Method:</label>
                    <select value={payForm.payment_method} onChange={e => setPayForm({ ...payForm, payment_method: e.target.value })} className="form-input">
                      {['Cash','Card','UPI','Net Banking','Cheque'].map(m => <option key={m}>{m}</option>)}
                    </select>
                  </div>
                  <div className="form-group"><label>Type:</label>
                    <select value={payForm.transaction_type} onChange={e => setPayForm({ ...payForm, transaction_type: e.target.value })} className="form-input">
                      {[['TUITION_FEE','Tuition Fee'],['BUS_FEE','Bus / Transport Fee'],['HOSTEL_FEE','Hostel Fee'],['LIBRARY_FEE','Library Fee'],['EXAM_FEE','Exam Fee'],['OTHER','Other']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                  <div className="form-group"><label>Remarks:</label><textarea value={payForm.remarks} onChange={e => setPayForm({ ...payForm, remarks: e.target.value })} className="form-textarea" rows="3"/></div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn-secondary" onClick={() => setShowPayment(false)}>Cancel</button>
                  <button type="submit" className="btn-success">Record Payment</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showPayrollDetail && selectedPayroll && (
          <PayrollDetailModal payroll={selectedPayroll} onClose={() => { setShowPayrollDetail(false); setSelectedPayroll(null); }} fmtCur={fmtCur} fmtDate={fmtDate} monthName={monthName}/>
        )}
        {showDetails && selectedUser && (
          <UserDetailsModal user={selectedUser} onClose={() => { setShowDetails(false); setSelectedUser(null); }} fmtCur={fmtCur} fmtDate={fmtDate}/>
        )}

        {showHprModal && selectedHpr && (
          <div className="modal-overlay" onClick={() => setShowHprModal(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Record Hostel Accessory Purchase — HPR-{selectedHpr.id}</h3>
                <button className="modal-close" onClick={() => setShowHprModal(false)}>&times;</button>
              </div>
              <form onSubmit={handleProcessHprSubmit}>
                <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  <div>
                    <strong>Room Number:</strong> {selectedHpr.room_number ? `Room ${selectedHpr.room_number}` : 'Bulk Stock'} ({selectedHpr.hostel_name})
                    <br/>
                    <strong>Accessory to Purchase:</strong> {selectedHpr.accessory_type_display} (Qty: {selectedHpr.quantity})
                  </div>

                  <div className="form-group">
                    <label>Vendor Name *</label>
                    <input
                      type="text"
                      className="form-input"
                      required
                      placeholder="e.g. Rama Electronics"
                      value={hprForm.vendor_name}
                      onChange={e => setHprForm({ ...hprForm, vendor_name: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label>Invoice Number *</label>
                    <input
                      type="text"
                      className="form-input"
                      required
                      placeholder="e.g. INV-2026-9876"
                      value={hprForm.invoice_number}
                      onChange={e => setHprForm({ ...hprForm, invoice_number: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label>Purchase Cost (₹) *</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-input"
                      required
                      placeholder="e.g. 1500.00"
                      value={hprForm.purchase_cost}
                      onChange={e => setHprForm({ ...hprForm, purchase_cost: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label>Purchase Date *</label>
                    <input
                      type="date"
                      className="form-input"
                      required
                      value={hprForm.purchase_date}
                      onChange={e => setHprForm({ ...hprForm, purchase_date: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label>Unique Asset Label / Code (Auto-Generated) *</label>
                    <input
                      type="text"
                      className="form-input"
                      required
                      readOnly
                      style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                      value={hprForm.new_asset_label}
                    />
                    <span style={{ fontSize: '11px', color: '#6b7280', display: 'block', marginTop: '2px' }}>
                      This label code will be automatically resolved by the backend system.
                    </span>
                  </div>

                  <div className="form-group">
                    <label>Warranty Details / Invoice Info</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="e.g. 2 years manufacturer warranty"
                      value={hprForm.warranty_details}
                      onChange={e => setHprForm({ ...hprForm, warranty_details: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label>Finance Notes / Audit Remarks</label>
                    <textarea
                      className="form-input"
                      rows="2"
                      placeholder="Processed and approved from emergency hostel maintenance fund."
                      value={hprForm.finance_notes}
                      onChange={e => setHprForm({ ...hprForm, finance_notes: e.target.value })}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn-secondary" onClick={() => setShowHprModal(false)}>Cancel</button>
                  <button type="submit" className="btn-success">✓ Record Purchase</button>
                </div>
              </form>
            </div>
          </div>
        )}

      </div>
    </DashboardLayout>
  );
};

/* ═══════════════════════════ SUB-COMPONENTS ════════════════════════════════ */

const UserDetailsModal = ({ user, onClose, fmtCur, fmtDate }) => {
  const currentMbbsYear = user.role === 'STUDENT' && user.profile?.course === 'MBBS' ? (user.current_mbbs_year || 1) : null;
  const [selectedMbbsYear, setSelectedMbbsYear] = useState(currentMbbsYear || 1);

  // Sync default selected tab when user opens
  useEffect(() => {
    if (currentMbbsYear) {
      setSelectedMbbsYear(currentMbbsYear);
    }
  }, [currentMbbsYear, user.id]);

  const r = user.financial_record;

  const hasActiveBus = (user.has_active_transport || !!user.transport_assignment) && ((user.bus_fee || 0) > 0 || (user.transport_assignment?.bus_fee || 0) > 0);
  const busFee = user.bus_fee || user.transport_assignment?.bus_fee || 0;

  // Helper for MBBS year labels
  const mbbsYearNames = {
    1: 'MBBS 1st Year (Pre-clinical)',
    2: 'MBBS 2nd Year (Para-clinical)',
    3: 'MBBS 3rd Year (Clinical)',
    4: 'MBBS 4th Year (Final MBBS)',
  };

  // Find fee record for selected MBBS year
  const getYearFeeRecord = (yr) => {
    return (user.academic_year_fees || []).find(
      (f) => f.course === 'MBBS' && (parseInt(f.mbbs_year, 10) === parseInt(yr, 10))
    );
  };

  // Filter transactions for selected year
  const getYearTransactions = (yr) => {
    return (user.transactions || []).filter(
      (tx) => (tx.mbbs_year && parseInt(tx.mbbs_year, 10) === parseInt(yr, 10)) ||
              (!tx.mbbs_year && yr === currentMbbsYear)
    );
  };

  const activeYearRecord = currentMbbsYear ? getYearFeeRecord(selectedMbbsYear) : null;
  const activeYearTxList = currentMbbsYear ? getYearTransactions(selectedMbbsYear) : [];

  const isCurrentMbbs = selectedMbbsYear === currentMbbsYear;

  // Base raw fee for active year
  const activeRecordFee = activeYearRecord ? activeYearRecord.total_fee : 0;
  const apiTuitionFee = user.tuition_fee ?? r?.tuition_fee ?? 0;
  const baseAcademicFee = user.academic_tuition_fee ?? (apiTuitionFee > 0 ? (apiTuitionFee - (hasActiveBus ? busFee : 0)) : 0);

  let yearTotalFee = 0;
  if (isCurrentMbbs) {
    if (apiTuitionFee > 0) {
      yearTotalFee = apiTuitionFee;
    } else if (activeRecordFee > 0) {
      yearTotalFee = activeRecordFee + (hasActiveBus ? busFee : 0);
    } else {
      yearTotalFee = baseAcademicFee + (hasActiveBus ? busFee : 0);
    }
  } else {
    yearTotalFee = activeRecordFee;
  }

  const yearBaseFee = (hasActiveBus && isCurrentMbbs) ? Math.max(0, yearTotalFee - busFee) : yearTotalFee;
  const yearPaid = activeYearRecord ? activeYearRecord.fee_paid : (isCurrentMbbs ? (user.tuition_paid || r?.tuition_paid || 0) : 0);
  const yearPending = Math.max(0, (yearTotalFee || 0) - (yearPaid || 0));
  const yearDeadline = activeYearRecord?.payment_deadline || (isCurrentMbbs ? (user.payment_deadline || r?.payment_deadline) : null);

  // Non-MBBS Dues
  const baseNonMbbsFee = user.academic_tuition_fee ?? (user.tuition_fee ? (user.tuition_fee - (hasActiveBus ? busFee : 0)) : (r?.tuition_fee || 0));
  const completeNonMbbsTotal = baseNonMbbsFee + (hasActiveBus ? busFee : 0);
  const nonMbbsPaid = user.tuition_paid ?? r?.tuition_paid ?? 0;
  const nonMbbsPending = Math.max(0, completeNonMbbsTotal - nonMbbsPaid);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 850 }}>
        <div className="modal-header">
          <div>
            <h2 style={{ margin: 0 }}>Financial Details — {user.full_name}</h2>
            <span style={{ fontSize: 13, color: '#6b7280' }}>
              ID: <strong>{user.enrollment_id}</strong> | {user.course_display || user.role_display}
            </span>
          </div>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        {/* MBBS Progressive Year Tabs - Compact One-Line Layout */}
        {user.role === 'STUDENT' && user.profile?.course === 'MBBS' && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'nowrap',
              overflowX: 'auto',
              borderBottom: '1px solid #e5e7eb',
              padding: '10px 20px',
              background: '#f9fafb',
              gap: 8,
              alignItems: 'center',
            }}
          >
            {[1, 2, 3, 4].map((yr) => {
              const isUnlocked = yr <= currentMbbsYear;
              const isCurrent = yr === currentMbbsYear;
              const isSelected = yr === selectedMbbsYear;

              return (
                <button
                  key={yr}
                  disabled={!isUnlocked}
                  onClick={() => isUnlocked && setSelectedMbbsYear(yr)}
                  title={!isUnlocked ? `Locked — Requires promotion to MBBS ${yr}${yr === 2 ? 'nd' : yr === 3 ? 'rd' : 'th'} Year` : ''}
                  style={{
                    padding: '6px 14px',
                    borderRadius: 20,
                    border: isSelected ? '1px solid #4f46e5' : '1px solid #e5e7eb',
                    background: isSelected ? '#4f46e5' : isUnlocked ? '#ffffff' : '#f3f4f6',
                    color: isSelected ? '#ffffff' : isUnlocked ? '#374151' : '#9ca3af',
                    fontWeight: isSelected ? 700 : 600,
                    opacity: isUnlocked ? 1 : 0.6,
                    cursor: isUnlocked ? 'pointer' : 'not-allowed',
                    fontSize: 12,
                    whiteSpace: 'nowrap',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    transition: 'all 0.15s ease',
                  }}
                >
                  <span>{isUnlocked ? (isCurrent ? '🟢' : '📜') : '🔒'}</span>
                  <span>MBBS {yr}{yr === 1 ? 'st' : yr === 2 ? 'nd' : yr === 3 ? 'rd' : 'th'} Year</span>
                  {isCurrent && (
                    <span
                      style={{
                        fontSize: 10,
                        background: isSelected ? 'rgba(255,255,255,0.25)' : '#dcfce7',
                        color: isSelected ? '#ffffff' : '#15803d',
                        padding: '1px 6px',
                        borderRadius: 10,
                        fontWeight: 700,
                      }}
                    >
                      Current
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        <div className="modal-body" style={{ padding: 24 }}>
          {user.role === 'STUDENT' && user.profile?.course === 'MBBS' ? (
            <div>
              {/* Summary Card for Selected Academic Year */}
              <div
                style={{
                  background: '#ffffff',
                  color: '#111827',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                  borderRadius: 14,
                  padding: 20,
                  marginBottom: 20,
                  display: 'flex',
                  justify: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 16,
                }}
              >
                <div>
                  <span style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#4f46e5' }}>
                    {mbbsYearNames[selectedMbbsYear]}
                  </span>
                  <h3 style={{ fontSize: 24, fontWeight: 800, margin: '4px 0 0 0', color: '#111827' }}>
                    Pending: {fmtCur(yearPending)}
                  </h3>
                  <p style={{ fontSize: 13, color: '#4b5563', margin: '4px 0 0 0' }}>
                    Total Structure Fee: {fmtCur(yearTotalFee)} | Paid Amount: {fmtCur(yearPaid)}
                  </p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <span
                    style={{
                      padding: '6px 14px',
                      borderRadius: 20,
                      fontSize: 13,
                      fontWeight: 700,
                      background: (yearPending || 0) <= 0 ? '#dcfce7' : '#fef3c7',
                      color: (yearPending || 0) <= 0 ? '#15803d' : '#b45309',
                    }}
                  >
                    {(yearPending || 0) <= 0 ? 'PAID / COMPLETED' : 'PENDING'}
                  </span>
                  {yearDeadline && (
                    <div style={{ fontSize: 12, color: '#c7d2fe', marginTop: 6 }}>
                      Deadline: {fmtDate(yearDeadline)}
                    </div>
                  )}
                </div>
              </div>

              {/* Personal & Financial Details Grid */}
              <div className="details-grid" style={{ marginBottom: 20 }}>
                <div className="details-section">
                  <h3 className="section-title">Student Info</h3>
                  {[
                    ['Full Name', user.full_name],
                    ['Student ID', user.enrollment_id],
                    ['Course', user.course_display],
                    ['Academic Year', `MBBS ${selectedMbbsYear}${selectedMbbsYear === 1 ? 'st' : selectedMbbsYear === 2 ? 'nd' : selectedMbbsYear === 3 ? 'rd' : 'th'} Year`],
                    ['Status', selectedMbbsYear === currentMbbsYear ? 'Active Student' : 'Promoted (Completed Year)'],
                  ].map(([l, v]) => (
                    <div className="detail-item" key={l}>
                      <span className="detail-label">{l}:</span>
                      <span className="detail-value">{v}</span>
                    </div>
                  ))}
                </div>

                <div className="details-section">
                  <h3 className="section-title">Year Dues Breakdown</h3>
                  {[
                    ['Academic Tuition Fee', fmtCur(yearBaseFee)],
                    hasActiveBus && isCurrentMbbs && ['Bus Fee (Transport)', fmtCur(busFee)],
                    ['Total Fee Structure', fmtCur(yearTotalFee)],
                    ['Paid Amount', fmtCur(yearPaid)],
                    ['Pending Dues', fmtCur(yearPending)],
                    ['Payment Deadline', fmtDate(yearDeadline)],
                  ].filter(Boolean).map(([l, v]) => (
                    <div className="detail-item" key={l}>
                      <span className="detail-label">{l}:</span>
                      <span className={`detail-value ${l.includes('Pending') && yearPending > 0 ? 'text-orange' : l.includes('Paid') ? 'text-green' : l.includes('Bus Fee') ? 'text-blue' : ''}`}>
                        {v}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Payment Receipts & Transaction Log for Selected Year */}
              <div className="details-section">
                <h3 className="section-title">
                  Receipts & Payment History — MBBS {selectedMbbsYear}{selectedMbbsYear === 1 ? 'st' : selectedMbbsYear === 2 ? 'nd' : selectedMbbsYear === 3 ? 'rd' : 'th'} Year
                </h3>
                {activeYearTxList.length === 0 ? (
                  <div style={{ padding: 16, textAlign: 'center', color: '#9ca3af', background: '#f9fafb', borderRadius: 8 }}>
                    No payment receipts recorded for MBBS {selectedMbbsYear}{selectedMbbsYear === 1 ? 'st' : selectedMbbsYear === 2 ? 'nd' : selectedMbbsYear === 3 ? 'rd' : 'th'} Year yet.
                  </div>
                ) : (
                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Transaction ID</th>
                          <th>Date</th>
                          <th>Amount</th>
                          <th>Mode</th>
                          <th>Status</th>
                          <th>Recorded By</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activeYearTxList.map((tx) => (
                          <tr key={tx.id}>
                            <td><strong>{tx.transaction_id}</strong></td>
                            <td>{fmtDate(tx.created_at)}</td>
                            <td className="text-green"><strong>{fmtCur(tx.amount)}</strong></td>
                            <td>{tx.payment_method || 'Cash'}</td>
                            <td>
                              <span className="badge badge-green">{tx.status_display || tx.status}</span>
                            </td>
                            <td>{tx.recorded_by_name || 'System'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Non-MBBS (PG, SSPG, Staff) View */
            <div className="details-grid">
              <div className="details-section">
                <h3 className="section-title">Personal & Role Info</h3>
                {[
                  ['Full Name', user.full_name],
                  ['Email', user.email],
                  ['Phone', user.phone_number || 'N/A'],
                  ['ID', user.enrollment_id],
                  ['Role', user.role_display],
                  user.role === 'STUDENT' && ['Course', user.course_display],
                ].filter(Boolean).map(([l, v]) => (
                  <div className="detail-item" key={l}>
                    <span className="detail-label">{l}:</span>
                    <span className="detail-value">{v}</span>
                  </div>
                ))}
              </div>

              <div className="details-section">
                <h3 className="section-title">Financial Details</h3>
                {user.role === 'STUDENT' ? (
                  [
                    ['Academic Tuition Fee', fmtCur(baseNonMbbsFee)],
                    hasActiveBus && ['Bus Fee (Transport)', fmtCur(busFee)],
                    ['Total Fee Structure', fmtCur(completeNonMbbsTotal)],
                    ['Paid Amount', fmtCur(nonMbbsPaid)],
                    ['Pending Dues', fmtCur(nonMbbsPending)],
                    ['Deadline', fmtDate(user.payment_deadline || r?.payment_deadline)],
                  ].filter(Boolean).map(([l, v]) => (
                    <div className="detail-item" key={l}>
                      <span className="detail-label">{l}:</span>
                      <span className={`detail-value ${l.includes('Pending') && nonMbbsPending > 0 ? 'text-orange' : l.includes('Paid') ? 'text-green' : l.includes('Bus Fee') ? 'text-blue' : ''}`}>
                        {v}
                      </span>
                    </div>
                  ))
                ) : (
                  r && [
                    ['Monthly Salary', fmtCur(r.monthly_salary)],
                    ['Last Paid', fmtDate(r.last_salary_paid)],
                  ].map(([l, v]) => (
                    <div className="detail-item" key={l}>
                      <span className="detail-label">{l}:</span>
                      <span className="detail-value">{v}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

const PayrollDetailModal = ({ payroll: p, onClose, fmtCur, fmtDate, monthName }) => (
  <div className="modal-overlay" onClick={onClose}>
    <div className="modal-content modal-large payroll-detail-modal" onClick={e => e.stopPropagation()}>
      <div className="modal-header"><h2>Payroll Details — {p.user_name}</h2><button className="modal-close" onClick={onClose}>&times;</button></div>
      <div className="modal-body">

        {/* Employee & period */}
        <div className="details-section">
          <h3 className="section-title">Employee & Period</h3>
          <div className="details-grid">
            {[['Name', p.user_name], ['Employee ID', p.user_employee_id],
              ['Designation', p.user_subrole || p.user_role],
              ['Period', p.from_date && p.to_date ? `${p.from_date}  →  ${p.to_date}` : `${monthName?.(p.month)} ${p.year}`],
              ['Working Days', `${p.working_days || 30} day(s) ${(p.working_days || 30) < 28 ? '⚡ partial period' : '✓ full month'}`],
            ].map(([l, v]) => <div className="detail-item" key={l}><span className="detail-label">{l}:</span><span className="detail-value">{v}</span></div>)}
          </div>
        </div>

        {/* Salary */}
        <div className="details-section">
          <h3 className="section-title">Salary Calculation</h3>
          <div className="salary-breakdown">
            <div className="breakdown-row"><span>Base Monthly Salary:</span><span className="amount">{fmtCur(p.base_salary)}</span></div>
            <div className="breakdown-row"><span>Daily Rate (÷ 30):</span><span className="amount">{fmtCur(p.daily_salary)}</span></div>
            <div className="breakdown-row highlight">
              <span>Period Gross ({p.working_days || 30} days × {fmtCur(p.daily_salary)}):</span>
              <span className="amount highlight-green">{fmtCur(p.gross_salary)}</span>
            </div>
          </div>
        </div>

        {/* Leave */}
        <div className="details-section">
          <h3 className="section-title">Leave & Attendance</h3>
          <div className="leave-summary">
            {[['Total Leave Days (in period)', p.total_leave_days, 'badge-gray'],
              ['Paid Leave Days', p.paid_leave_days, 'badge-green'],
              ['LOP Days (unpaid)', p.unpaid_leave_days, 'badge-red'],
              ['Late Arrivals', p.late_days || 0, 'badge-orange'],
              ['Late Penalty Days', p.late_penalty_days || 0, 'badge-red'],
            ].map(([l, v, c]) => (
              <div className="summary-row" key={l}><span>{l}:</span><span className={`badge ${c}`}>{v}</span></div>
            ))}
          </div>
        </div>

        {/* Leave detail table */}
        {p.leave_details?.length > 0 && (
          <div className="details-section">
            <h3 className="section-title">Leave Breakdown (within this period)</h3>
            <div className="table-container">
              <table className="data-table leave-detail-table">
                <thead><tr><th>Type</th><th>From</th><th>To</th><th>Days in Period</th><th>Status</th><th>Deduction</th></tr></thead>
                <tbody>
                  {p.leave_details.map((d, i) => (
                    <tr key={i}>
                      <td><strong>{d.leave_type_display}</strong></td>
                      <td>{fmtDate(d.from_date)}</td><td>{fmtDate(d.to_date)}</td>
                      <td>{d.days_in_month}</td>
                      <td><span className={`badge ${d.is_paid ? 'badge-green' : 'badge-red'}`}>{d.is_paid ? 'Paid' : 'LOP'}</span></td>
                      <td className={d.deduction_amount > 0 ? 'text-red' : ''}>{fmtCur(d.deduction_amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Final */}
        <div className="details-section">
          <h3 className="section-title">Final Calculation</h3>
          <div className="final-amount">
            <div className="amount-row"><span>Period Gross ({p.working_days || 30} days):</span><span>{fmtCur(p.gross_salary)}</span></div>
            <div className="amount-row"><span>Leave Deduction ({p.unpaid_leave_days} × {fmtCur(p.daily_salary)}):</span><span className="text-red">– {fmtCur(p.leave_deduction)}</span></div>
            <div className="amount-row"><span>Late Penalty ({p.late_penalty_days || 0} days):</span><span className="text-red">– {fmtCur(p.late_deduction || 0)}</span></div>
            <div className="amount-row"><span>Other Deductions:</span><span className="text-red">– {fmtCur(p.other_deductions)}</span></div>
            <div className="amount-row final">
              <span><strong>Net Salary:</strong></span>
              <span className="highlight-green"><strong>{fmtCur(p.net_salary)}</strong></span>
            </div>
          </div>
        </div>

        {/* Meta */}
        <div className="details-section">
          <h3 className="section-title">Processing Info</h3>
          <div className="details-grid">
            {[['Status', p.status_display],['Calculated By', p.calculated_by_name || 'System'],
              ['Calculated At', fmtDate(p.calculated_at)],
              p.approved_by_name && ['Approved By', p.approved_by_name],
            ].filter(Boolean).map(([l, v]) => (
              <div className="detail-item" key={l}><span className="detail-label">{l}:</span><span className="detail-value">{v}</span></div>
            ))}
          </div>
        </div>
      </div>
      <div className="modal-footer"><button className="btn-secondary" onClick={onClose}>Close</button></div>
    </div>
  </div>
);


/* ─────────────────────────────────────────────────────────────────────────── */
/*  FinanceReportsAnalyticsTab — Financial View, Overdue Dues & Exports        */
/* ─────────────────────────────────────────────────────────────────────────── */

function FinanceReportsAnalyticsTab({ stats, onPayStudent }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Real database states
  const [categorizedData, setCategorizedData] = useState({});
  const [expenseSummary, setExpenseSummary] = useState({});
  const [overdueStudents, setOverdueStudents] = useState([]);
  const [approvedPayrollTotal, setApprovedPayrollTotal] = useState(0);
  const [approvedPayrollList, setApprovedPayrollList] = useState([]);
  const [approvedExpenseList, setApprovedExpenseList] = useState([]);
  const [approvedVehicleExpenseTotal, setApprovedVehicleExpenseTotal] = useState(0);
  const [approvedVehicleExpenseList, setApprovedVehicleExpenseList] = useState([]);

  // Deductions Ledger Modal State
  const [showDeductionsModal, setShowDeductionsModal] = useState(false);
  const [deductionsFilter, setDeductionsFilter] = useState('ALL'); // 'ALL', 'EXPENSE', 'PAYROLL', 'VEHICLE_EXPENSE'
  const [deductionsSearch, setDeductionsSearch] = useState('');
  const [deductionsFromDate, setDeductionsFromDate] = useState('');
  const [deductionsToDate, setDeductionsToDate] = useState('');
  const [deductionsPage, setDeductionsPage] = useState(1);
  
  // View mode & category states (matching Admin Financial Analytics Tab)
  const [viewMode, setViewMode] = useState('dashboard'); // 'dashboard' or 'tabular'
  const [revenueCategory, setRevenueCategory] = useState('ALL'); // 'ALL', 'HOSPITAL', 'COLLEGE', 'HOSTEL'
  
  // Overdue Modal State
  const [showOverdueModal, setShowOverdueModal] = useState(false);
  const [overdueSearch, setOverdueSearch] = useState('');
  
  // Fine Input Modal State
  const [fineTargetUser, setFineTargetUser] = useState(null);
  const [fineInput, setFineInput] = useState('');
  const [savingFine, setSavingFine] = useState(false);

  // Filter states for Tabular View
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchRealAnalyticsData();
  }, []);

  const fetchRealAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [catRes, expRes, studRes, payRollRes, rawExpRes, vehExpRes] = await Promise.allSettled([
        adminAPI.getAnalyticsData(),
        expenseAPI.getExpenseSummary(),
        financeAPI.getUsers({ role: 'STUDENT' }),
        payrollAPI ? payrollAPI.getPayrollRecords() : Promise.resolve([]),
        expenseAPI ? expenseAPI.getExpenses() : Promise.resolve([]),
        vehicleExpenseAPI ? vehicleExpenseAPI.getExpenses({ status: 'ALL' }) : Promise.resolve([])
      ]);

      if (catRes.status === 'fulfilled') setCategorizedData(catRes.value?.categorized_financials || {});
      if (expRes.status === 'fulfilled') setExpenseSummary(expRes.value || {});
      
      // Calculate approved payroll payouts
      if (payRollRes.status === 'fulfilled') {
        const pList = Array.isArray(payRollRes.value) ? payRollRes.value : (payRollRes.value?.results || []);
        const filteredPList = pList.filter(r => r.status === 'APPROVED' || r.status === 'PAID');
        const approvedSum = filteredPList.reduce((a, b) => a + (parseFloat(b.net_salary || 0)), 0);
        setApprovedPayrollTotal(approvedSum);
        setApprovedPayrollList(filteredPList);
      }

      if (rawExpRes.status === 'fulfilled') {
        const eList = Array.isArray(rawExpRes.value) ? rawExpRes.value : (rawExpRes.value?.results || []);
        setApprovedExpenseList(eList);
      }

      // Calculate approved vehicle maintenance & renewal expenses
      if (vehExpRes.status === 'fulfilled') {
        const vList = Array.isArray(vehExpRes.value) ? vehExpRes.value : (vehExpRes.value?.results || []);
        const approvedVList = vList.filter(v => v.status === 'APPROVED' || v.status === 'COMPLETED');
        const approvedVSum = approvedVList.reduce((a, b) => a + (parseFloat(b.approved_amount || b.requested_amount || 0)), 0);
        setApprovedVehicleExpenseTotal(approvedVSum);
        setApprovedVehicleExpenseList(approvedVList);
      }

      if (studRes.status === 'fulfilled') {
        const sList = Array.isArray(studRes.value) ? studRes.value : (studRes.value?.results || []);
        const todayStr = new Date().toISOString().slice(0, 10);
        const overdueOnlyList = sList.filter(s => {
          const fr = s.financial_record || {};
          const pend = parseFloat(s.tuition_pending ?? fr.tuition_pending ?? 0);
          const deadline = s.payment_deadline || fr.payment_deadline || '';
          const isOverdueFlag = s.is_overdue || fr.is_overdue;
          if (pend <= 0) return false;
          if (isOverdueFlag) return true;
          if (deadline && deadline !== 'N/A' && deadline < todayStr) return true;
          return false;
        });
        setOverdueStudents(overdueOnlyList);
      }
    } catch (err) {
      console.error('Failed to load financial analytics:', err);
      setError('Failed to fetch financial analytics from database.');
    } finally {
      setLoading(false);
    }
  };

  // Paid-only transaction extraction per stream
  const hospTxns = (categorizedData.hospital_data?.transactions || []).filter(t => t.status === 'PAID' || t.status === 'SUCCESS');
  const collTxns = (categorizedData.college_data?.transactions || []).filter(t => t.status === 'PAID' || t.status === 'SUCCESS');
  const hostTxns = (categorizedData.hostel_data?.transactions || []).filter(t => t.status === 'PAID' || t.status === 'SUCCESS');

  const hospTotal = hospTxns.reduce((a, b) => a + (b.amount_val || 0), 0);
  const collTotal = collTxns.reduce((a, b) => a + (b.amount_val || 0), 0);
  const hostTotal = hostTxns.reduce((a, b) => a + (b.amount_val || 0), 0);
  const grossCollected = hospTotal + collTotal + hostTotal;

  // Real Approved Expenses, Approved Payroll Payouts & Approved Vehicle Expenses
  const operationalExpenses = parseFloat(expenseSummary?.total_expenses || 0);
  const totalApprovedOutflows = approvedPayrollTotal + operationalExpenses + approvedVehicleExpenseTotal;
  const netSurplus = grossCollected - totalApprovedOutflows;

  // Deductions List Construction (Expenses + Payroll + Vehicle Expenses)
  const expenseDeductions = approvedExpenseList.map(e => ({
    id: `EXP-${e.id}`,
    type: 'EXPENSE',
    type_label: '💸 Operational Expense',
    date: e.expense_date || e.created_at?.slice(0, 10) || e.date || 'N/A',
    payee: e.vendor_name || e.paid_to || e.created_by_name || 'Vendor / Department',
    category: e.category_display || e.category || e.expense_type || 'General Expense',
    description: e.description || e.notes || e.title || e.category || 'Approved Operational Cost',
    amount: parseFloat(e.amount || 0),
    method: e.payment_method || 'Cash / Transfer',
    status: e.status || 'APPROVED'
  }));

  const payrollDeductions = approvedPayrollList.map(p => ({
    id: `PAY-${p.id}`,
    type: 'PAYROLL',
    type_label: '💼 Payroll Payout',
    date: p.to_date || p.calculated_at?.slice(0, 10) || `${p.year}-${String(p.month).padStart(2, '0')}-28`,
    payee: p.user_name || `Employee #${p.user}`,
    category: 'Salary Payout',
    description: `Monthly Payroll for ${p.month}/${p.year} (${p.working_days || 30} days)`,
    amount: parseFloat(p.net_salary || 0),
    method: 'Direct Bank Transfer',
    status: p.status_display || p.status || 'APPROVED'
  }));

  const vehicleDeductions = approvedVehicleExpenseList.map(v => ({
    id: `VEH-${v.request_id || v.id}`,
    type: 'VEHICLE_EXPENSE',
    type_label: '🚌 Vehicle Expense',
    date: v.approved_at?.slice(0, 10) || v.created_at?.slice(0, 10) || 'N/A',
    payee: v.vendor_name || v.created_by_name || 'Transport Dept / Vendor',
    category: v.expense_category_display || v.category_display || v.expense_category || 'Vehicle Maintenance',
    description: `${v.vehicle_registration_number || 'Vehicle'} — ${v.expense_description || v.vendor_name || 'Approved Vehicle Cost'}`,
    amount: parseFloat(v.approved_amount || v.requested_amount || 0),
    method: 'Finance Approved Release',
    status: v.status_display || v.status || 'APPROVED'
  }));

  const allDeductions = [...expenseDeductions, ...payrollDeductions, ...vehicleDeductions].sort((a, b) => b.date.localeCompare(a.date));

  // Base date & search filtered deductions (evaluated BEFORE tab filter for dynamic counts)
  const dateAndSearchFilteredDeductions = allDeductions.filter(d => {
    if (deductionsFromDate && d.date && d.date < deductionsFromDate) return false;
    if (deductionsToDate && d.date && d.date > deductionsToDate) return false;
    if (!deductionsSearch) return true;
    const q = deductionsSearch.toLowerCase();
    return d.id.toLowerCase().includes(q) ||
           d.payee.toLowerCase().includes(q) ||
           d.category.toLowerCase().includes(q) ||
           d.description.toLowerCase().includes(q);
  });

  // Dynamic counts for tab buttons based on current date range & search filter
  const dateFilteredAllCount = dateAndSearchFilteredDeductions.length;
  const dateFilteredExpenseCount = dateAndSearchFilteredDeductions.filter(d => d.type === 'EXPENSE').length;
  const dateFilteredPayrollCount = dateAndSearchFilteredDeductions.filter(d => d.type === 'PAYROLL').length;
  const dateFilteredVehicleCount = dateAndSearchFilteredDeductions.filter(d => d.type === 'VEHICLE_EXPENSE').length;

  // Final table list filtered by active tab
  const modalFilteredDeductions = dateAndSearchFilteredDeductions.filter(d => {
    if (deductionsFilter !== 'ALL' && d.type !== deductionsFilter) return false;
    return true;
  });

  const filteredOutflowsTotal = modalFilteredDeductions.reduce((a, b) => a + b.amount, 0);

  const DEDUCTIONS_PER_PAGE = 10;
  const totalDeductionPages = Math.ceil(modalFilteredDeductions.length / DEDUCTIONS_PER_PAGE) || 1;
  const safeDeductionsPage = Math.min(deductionsPage, totalDeductionPages);
  const pagedDeductions = modalFilteredDeductions.slice(
    (safeDeductionsPage - 1) * DEDUCTIONS_PER_PAGE,
    safeDeductionsPage * DEDUCTIONS_PER_PAGE
  );

  const allPaidTxns = [...hospTxns, ...collTxns, ...hostTxns];

  // Category Active Selection
  let activeTxnsList = allPaidTxns;
  if (revenueCategory === 'HOSPITAL') activeTxnsList = hospTxns;
  else if (revenueCategory === 'COLLEGE') activeTxnsList = collTxns;
  else if (revenueCategory === 'HOSTEL') activeTxnsList = hostTxns;

  const activeCategoryTotal = activeTxnsList.reduce((a, b) => a + (b.amount_val || 0), 0);

  // Filter by date range and search query
  const filteredTxns = activeTxnsList.filter(t => {
    const matchesSearch = !searchQuery ||
      (t.id && t.id.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (t.customer && t.customer.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (t.source && t.source.toLowerCase().includes(searchQuery.toLowerCase()));

    let matchesDate = true;
    if (startDate && t.date) matchesDate = matchesDate && t.date >= startDate;
    if (endDate && t.date) matchesDate = matchesDate && t.date <= endDate;

    return matchesSearch && matchesDate;
  });

  const filteredTotalAmount = filteredTxns.reduce((a, b) => a + (b.amount_val || 0), 0);

  // CSV Export
  const exportCSV = () => {
    if (!filteredTxns.length) {
      notify.info("No transaction records available to export for current filters.");
      return;
    }
    const csvContent = "data:text/csv;charset=utf-8,"
      + ["Transaction ID,Date,Customer/Patient/Student,Revenue Source,Amount (INR),Payment Method,Status",
         ...filteredTxns.map(e => `"${e.id}","${e.date}","${e.customer || ''}","${e.source || ''}","${e.amount_val || 0}","${e.method || ''}","PAID"`)]
        .join("\n");
    const link = document.createElement("a");
    link.setAttribute("href", encodeURI(csvContent));
    link.setAttribute("download", `Financial_Report_${revenueCategory}_${viewMode}_${new Date().toISOString().slice(0,10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleAddFineSubmit = async (e) => {
    e.preventDefault();
    if (!fineTargetUser || !fineInput || parseFloat(fineInput) <= 0) {
      notify.error("Please enter a valid fine amount greater than 0.");
      return;
    }
    try {
      setSavingFine(true);
      await financeAPI.updateFinancialRecord(fineTargetUser.id, { fine_amount: parseFloat(fineInput) });
      notify.success(`Fine of ₹${parseFloat(fineInput).toLocaleString()} added to ${fineTargetUser.first_name} ${fineTargetUser.last_name}.`);
      setFineTargetUser(null);
      setFineInput('');
      fetchRealAnalyticsData();
    } catch (err) {
      notify.error(err.response?.data?.error || "Failed to add fine.");
    } finally {
      setSavingFine(false);
    }
  };

  const modalFilteredStudents = overdueStudents.filter(s => {
    if (!overdueSearch) return true;
    const q = overdueSearch.toLowerCase();
    const name = `${s.first_name || ''} ${s.last_name || ''}`.toLowerCase();
    const email = (s.email || '').toLowerCase();
    const roll = (s.profile?.student_id || '').toLowerCase();
    return name.includes(q) || email.includes(q) || roll.includes(q);
  });

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>
        <div style={{ fontSize: 24, marginBottom: 8 }}>📊</div>
        <div style={{ fontWeight: 600 }}>Fetching real financial records from database...</div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`
        @media print {
          body * { visibility: hidden; }
          .rg-print-area, .rg-print-area * { visibility: visible; }
          .rg-print-area { position: absolute; left: 0; top: 0; width: 100%; }
          .rg-no-print { display: none !important; }
        }
      `}</style>

      {/* Action Header Bar matching Admin Dashboard */}
      <div className="rg-no-print" style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <label style={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>Revenue Category:</label>
          <select 
            value={revenueCategory} 
            onChange={e => setRevenueCategory(e.target.value)}
            style={{ padding: '7px 14px', border: '1px solid #1d4ed8', borderRadius: 8, background: '#eff6ff', color: '#1d4ed8', fontWeight: 800, fontSize: 13, cursor: 'pointer', outline: 'none' }}
          >
            <option value="ALL">🌐 All Revenue Streams</option>
            <option value="HOSPITAL">🏥 Hospital Revenue (OPD & Diagnostics)</option>
            <option value="COLLEGE">🎓 College Revenue (Tuition & Student Fees)</option>
            <option value="HOSTEL">🏨 Hostel Revenue (Deposits & Fees)</option>
          </select>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {/* View Mode Toggle Switcher */}
          <div style={{ display: 'inline-flex', border: '1px solid #cbd5e1', borderRadius: 8, overflow: 'hidden', background: '#f8fafc', padding: 2 }}>
            <button 
              onClick={() => setViewMode('dashboard')}
              style={{ padding: '6px 14px', border: 'none', background: viewMode === 'dashboard' ? '#ffffff' : 'transparent', color: viewMode === 'dashboard' ? '#1d4ed8' : '#64748b', fontWeight: 800, fontSize: 12, borderRadius: 6, cursor: 'pointer', boxShadow: viewMode === 'dashboard' ? '0 1px 2px rgba(0,0,0,0.06)' : 'none' }}
            >
              📊 Analytics View
            </button>
            <button 
              onClick={() => setViewMode('tabular')}
              style={{ padding: '6px 14px', border: 'none', background: viewMode === 'tabular' ? '#ffffff' : 'transparent', color: viewMode === 'tabular' ? '#1d4ed8' : '#64748b', fontWeight: 800, fontSize: 12, borderRadius: 6, cursor: 'pointer', boxShadow: viewMode === 'tabular' ? '0 1px 2px rgba(0,0,0,0.06)' : 'none' }}
            >
              📋 Tabular View ({filteredTxns.length})
            </button>
          </div>

          <button onClick={exportCSV} style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #1d4ed8', background: '#eff6ff', fontSize: 12, fontWeight: 700, color: '#1d4ed8', cursor: 'pointer' }}>
            📥 Export CSV
          </button>
          <button onClick={handlePrint} style={{ padding: '7px 14px', borderRadius: 8, border: 'none', background: '#1d4ed8', fontSize: 12, fontWeight: 800, color: '#ffffff', cursor: 'pointer', boxShadow: '0 2px 6px rgba(29,78,216,0.3)' }}>
            🖨 Print Report
          </button>
        </div>
      </div>

      <div className="rg-print-area" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* Print Letterhead (Visible in Print View) */}
        <div style={{ display: 'none' }} className="rg-print-only">
          <div style={{ textAlign: 'center', borderBottom: '2px solid #0f172a', paddingBottom: 12, marginBottom: 20 }}>
            <h1 style={{ fontSize: 20, fontWeight: 900, color: '#0f172a', margin: 0, textTransform: 'uppercase' }}>Medical College & Hospital ERP</h1>
            <p style={{ fontSize: 12, color: '#475569', margin: '4px 0 0' }}>Official Financial Audit & Stream Revenue Compliance Statement</p>
            <p style={{ fontSize: 11, color: '#64748b', margin: '2px 0 0' }}>Generated on: {new Date().toLocaleString()} · Stream: {revenueCategory} · Mode: {viewMode}</p>
          </div>
        </div>

        {/* ── VIEW MODE 1: ANALYTICS DASHBOARD VIEW ── */}
        {viewMode === 'dashboard' && (() => {
          let barItems = [];
          if (revenueCategory === 'HOSPITAL') {
            const labAmt = hospTxns.filter(t => (t.id || '').startsWith('LAB') || (t.source || '').toLowerCase().includes('lab')).reduce((a, b) => a + (b.amount_val || 0), 0);
            const opdAmt = hospTxns.filter(t => (t.id || '').startsWith('OPD') || (t.source || '').toLowerCase().includes('opd')).reduce((a, b) => a + (b.amount_val || 0), 0);

            barItems = [
              { label: 'Lab Diagnostics', value: labAmt, formattedValue: `₹${labAmt.toLocaleString()}`, color: '#3b82f6' },
              { label: 'OPD Consultations', value: opdAmt, formattedValue: `₹${opdAmt.toLocaleString()}`, color: '#84cc16' }
            ];
          } else if (revenueCategory === 'COLLEGE') {
            const mbbs1 = collTxns.filter(t => (t.source || '').includes('1')).reduce((a, b) => a + (b.amount_val || 0), 0);
            const mbbsSenior = collTxns.filter(t => !(t.source || '').includes('1')).reduce((a, b) => a + (b.amount_val || 0), 0);

            barItems = [
              { label: 'MBBS 1st Year Tuition', value: mbbs1, formattedValue: `₹${mbbs1.toLocaleString()}`, color: '#3b82f6' },
              { label: 'Senior Batches & PG Fees', value: mbbsSenior, formattedValue: `₹${mbbsSenior.toLocaleString()}`, color: '#84cc16' }
            ];
          } else if (revenueCategory === 'HOSTEL') {
            const depAmt = hostTxns.filter(t => (t.source || '').includes('Deposit')).reduce((a, b) => a + (b.amount_val || 0), 0);
            const feeAmt = hostTxns.filter(t => !(t.source || '').includes('Deposit')).reduce((a, b) => a + (b.amount_val || 0), 0);

            barItems = [
              { label: 'Security Deposits', value: depAmt, formattedValue: `₹${depAmt.toLocaleString()}`, color: '#3b82f6' },
              { label: 'Monthly Room Rent', value: feeAmt, formattedValue: `₹${feeAmt.toLocaleString()}`, color: '#84cc16' }
            ];
          } else {
            barItems = [
              { label: 'Hospital Revenue', value: hospTotal, formattedValue: `₹${hospTotal.toLocaleString()}`, color: '#3b82f6' },
              { label: 'College Revenue', value: collTotal, formattedValue: `₹${collTotal.toLocaleString()}`, color: '#84cc16' },
              { label: 'Hostel Revenue', value: hostTotal, formattedValue: `₹${hostTotal.toLocaleString()}`, color: '#f59e0b' }
            ];
          }

          const maxVal = Math.max(...barItems.map(b => b.value), 100);
          const step = maxVal / 4;
          const yAxisTicks = [
            '0',
            Math.round(step).toLocaleString(),
            Math.round(step * 2).toLocaleString(),
            Math.round(step * 3).toLocaleString(),
            Math.round(maxVal).toLocaleString()
          ];

          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              
              {/* Summary Cards Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
                
                {/* Gross Revenue Card */}
                <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '18px 20px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Gross Revenue ({revenueCategory})</div>
                  <div style={{ fontSize: 24, fontWeight: 900, color: '#0f172a', marginTop: 6 }}>₹{activeCategoryTotal.toLocaleString()}</div>
                </div>

                {/* Net Surplus Card (Interactive Modal Trigger) */}
                <div
                  onClick={() => setShowDeductionsModal(true)}
                  style={{
                    background: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: 12,
                    padding: '18px 20px',
                    display: 'flex',
                    flexDirection: 'column',
                    justify: 'space-between',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
                  }}
                  title="Click to view all money deductions & transaction details"
                >
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Net Surplus (Available Balance)</div>
                      <span style={{ fontSize: 10, fontWeight: 800, background: '#f1f5f9', color: '#1d4ed8', padding: '2px 8px', borderRadius: 12 }}>
                        👁 View Outflows
                      </span>
                    </div>
                    <div style={{ fontSize: 24, fontWeight: 900, color: netSurplus >= 0 ? '#166534' : '#991b1b', marginTop: 6 }}>
                      {netSurplus >= 0 ? '+' : ''}₹{netSurplus.toLocaleString()}
                    </div>
                  </div>

                  <div style={{ marginTop: 10, fontSize: 11, fontWeight: 700, color: '#1d4ed8', display: 'flex', alignItems: 'center', gap: 4 }}>
                    Click to view {allDeductions.length} money deductions & transaction ledger →
                  </div>
                </div>

                {/* Overdue Fee Dues & Risk Alert Card */}
                <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '18px 20px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <h3 style={{ fontSize: 14, fontWeight: 800, color: '#0f172a', margin: 0 }}>🚨 Overdue Fee Dues</h3>
                      <span style={{ fontSize: 11, fontWeight: 800, background: '#fee2e2', color: '#991b1b', padding: '2px 8px', borderRadius: 12 }}>
                        {overdueStudents.length} Overdue Accounts
                      </span>
                    </div>

                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 8 }}>
                      Total Outstanding Dues: <strong style={{ color: '#991b1b', fontSize: 15 }}>₹{overdueStudents.reduce((a, b) => a + parseFloat(b.tuition_pending ?? b.financial_record?.tuition_pending ?? 0), 0).toLocaleString()}</strong>
                    </div>
                  </div>

                  <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => setShowOverdueModal(true)}
                      style={{
                        padding: '7px 14px',
                        borderRadius: 8,
                        border: '1px solid #cbd5e1',
                        background: '#f8fafc',
                        color: '#1d4ed8',
                        fontSize: 12,
                        fontWeight: 800,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6
                      }}
                    >
                      👁 View All Overdue Students ({overdueStudents.length})
                    </button>
                  </div>
                </div>

              </div>

              {/* Vertical Bar Chart Dashboard matching Admin Dashboard */}
              <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                  <div>
                    <h3 style={{ fontSize: 15, fontWeight: 800, color: '#0f172a', margin: 0 }}>📊 Revenue Distribution & Stream Comparison ({revenueCategory})</h3>
                    <p style={{ fontSize: 12, color: '#64748b', margin: '2px 0 0' }}>Dynamic visual bar chart computed from verified database records</p>
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 800, background: '#eff6ff', color: '#1d4ed8', padding: '4px 12px', borderRadius: 20 }}>
                    Category Total: ₹{activeCategoryTotal.toLocaleString()}
                  </span>
                </div>

                {/* Chart Area */}
                <div style={{ position: 'relative', height: 260, marginTop: 14, display: 'flex' }}>
                  {/* Y-Axis Labels */}
                  <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', paddingRight: 14, fontSize: 11, fontWeight: 700, color: '#94a3b8', textAlign: 'right', minWidth: 48, userSelect: 'none' }}>
                    <span>{yAxisTicks[4]}</span>
                    <span>{yAxisTicks[3]}</span>
                    <span>{yAxisTicks[2]}</span>
                    <span>{yAxisTicks[1]}</span>
                    <span>{yAxisTicks[0]}</span>
                  </div>

                  {/* Chart Grid Lines & Vertical Bars */}
                  <div style={{ flex: 1, position: 'relative', borderLeft: '1px solid #e2e8f0', borderBottom: '1px solid #cbd5e1', height: 210 }}>
                    {[0, 25, 50, 75, 100].map(pct => (
                      <div key={pct} style={{ position: 'absolute', bottom: `${pct}%`, left: 0, right: 0, height: 1, background: pct === 0 ? 'transparent' : '#f1f5f9' }} />
                    ))}

                    <div style={{ position: 'absolute', inset: 0, display: 'flex', justifyContent: 'space-around', alignItems: 'flex-end', padding: '0 16px 4px' }}>
                      {barItems.map((bar, idx) => {
                        const chartHeightPx = 150;
                        const barHeightPx = maxVal > 0 ? Math.max(14, Math.round((bar.value / maxVal) * chartHeightPx)) : 14;
                        return (
                          <div key={bar.label + idx} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', minWidth: 54, position: 'relative' }}>
                            <span style={{ fontSize: 11, fontWeight: 800, color: '#0f172a', marginBottom: 6, whiteSpace: 'nowrap' }}>
                              {bar.formattedValue}
                            </span>

                            <div 
                              style={{
                                width: 48,
                                height: barHeightPx,
                                minHeight: 14,
                                backgroundColor: bar.color,
                                borderTopLeftRadius: 6,
                                borderTopRightRadius: 6,
                                boxShadow: '0 3px 6px rgba(0,0,0,0.15)',
                                transition: 'height 0.4s ease-in-out'
                              }} 
                              title={`${bar.label}: ${bar.formattedValue}`}
                            />

                            <span style={{ fontSize: 11, fontWeight: 700, color: '#475569', marginTop: 10, textAlign: 'center', lineHeight: 1.2 }}>
                              {bar.label}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

            </div>
          );
        })()}

        {/* ── VIEW MODE 2: TABULAR LEDGER VIEW ── */}
        {viewMode === 'tabular' && (
          <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
            
            {/* Table Filters Bar */}
            <div className="rg-no-print" style={{ padding: '14px 18px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>Verified Collected Transactions ({revenueCategory})</span>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={{ padding: '5px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12 }} title="Start Date" />
                <span style={{ fontSize: 12, color: '#94a3b8' }}>to</span>
                <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={{ padding: '5px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12 }} title="End Date" />
                
                <input type="search" placeholder="Search ID, Name, Source..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} style={{ padding: '6px 12px', border: '1px solid #cbd5e1', borderRadius: 7, fontSize: 12, background: '#ffffff', width: 180 }} />
              </div>
            </div>

            {/* Transaction Ledger Table */}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, textAlign: 'left' }}>
                <thead>
                  <tr style={{ background: '#f1f5f9', color: '#475569', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>
                    <th style={{ padding: '10px 14px' }}>Transaction ID</th>
                    <th style={{ padding: '10px 14px' }}>Date</th>
                    <th style={{ padding: '10px 14px' }}>Customer / Patient / Student</th>
                    <th style={{ padding: '10px 14px' }}>Revenue Source</th>
                    <th style={{ padding: '10px 14px' }}>Collected Amount</th>
                    <th style={{ padding: '10px 14px' }}>Payment Method</th>
                    <th style={{ padding: '10px 14px' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTxns.length === 0 ? (
                    <tr>
                      <td colSpan="7" style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>
                        No verified paid transactions found matching the selected filters.
                      </td>
                    </tr>
                  ) : (
                    filteredTxns.map(t => (
                      <tr key={t.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '10px 14px', fontWeight: 800, color: '#0f172a' }}>{t.id}</td>
                        <td style={{ padding: '10px 14px', color: '#64748b', whiteSpace: 'nowrap' }}>{t.date}</td>
                        <td style={{ padding: '10px 14px', color: '#334155', fontWeight: 600 }}>{t.customer}</td>
                        <td style={{ padding: '10px 14px', color: '#475569' }}>{t.source}</td>
                        <td style={{ padding: '10px 14px', fontWeight: 800, color: '#166534' }}>₹{(t.amount_val || 0).toLocaleString()}</td>
                        <td style={{ padding: '10px 14px', color: '#475569' }}>{t.method}</td>
                        <td style={{ padding: '10px 14px' }}>
                          <span style={{ padding: '3px 8px', borderRadius: 12, fontSize: 10, fontWeight: 400, background: '#dcfce7', color: '#166534' }}>
                            PAID
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                <tfoot>
                  <tr style={{ background: '#f8fafc', borderTop: '2px solid #e2e8f0', fontWeight: 800 }}>
                    <td colSpan="4" style={{ padding: '12px 14px', textAlign: 'right', color: '#0f172a' }}>Filter Total Collected:</td>
                    <td colSpan="3" style={{ padding: '12px 14px', color: '#166534', fontSize: 14 }}>₹{filteredTotalAmount.toLocaleString()}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

          </div>
        )}

      </div>

      {/* ── OVERDUE STUDENTS MODAL WITH PAY & ADD FINE ACTIONS ── */}
      {showOverdueModal && (
        <div
          onClick={() => setShowOverdueModal(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: '#ffffff', borderRadius: 16, width: '100%', maxWidth: 840, maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 50px rgba(0,0,0,0.2)', overflow: 'hidden' }}
          >
            {/* Modal Header */}
            <div style={{ padding: '16px 22px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fff7f7' }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#991b1b' }}>🚨 Overdue Fee Dues — Student List ({overdueStudents.length})</div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                  Record payments or add late fines directly to overdue student accounts
                </div>
              </div>
              <button onClick={() => setShowOverdueModal(false)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#94a3b8', lineHeight: 1 }}>×</button>
            </div>

            {/* Modal Search Bar */}
            <div style={{ padding: '12px 22px', borderBottom: '1px solid #f1f5f9', background: '#ffffff' }}>
              <input
                type="search"
                placeholder="Search student name, email, roll number..."
                value={overdueSearch}
                onChange={e => setOverdueSearch(e.target.value)}
                style={{ width: '100%', padding: '8px 14px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13 }}
              />
            </div>

            {/* Modal Table Body */}
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 22px 16px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, textAlign: 'left', marginTop: 12 }}>
                <thead>
                  <tr style={{ background: '#f8fafc', color: '#64748b', fontSize: 11, fontWeight: 500, textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0' }}>
                    <th style={{ padding: '10px 12px' }}>Student Name</th>
                    <th style={{ padding: '10px 12px' }}>Student ID / Course</th>
                    <th style={{ padding: '10px 12px' }}>Pending Amount</th>
                    <th style={{ padding: '10px 12px' }}>Deadline</th>
                    <th style={{ padding: '10px 12px', textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {modalFilteredStudents.length === 0 ? (
                    <tr>
                      <td colSpan="5" style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>
                        {overdueStudents.length === 0 ? 'No overdue student accounts found in the database.' : 'No matching overdue students found.'}
                      </td>
                    </tr>
                  ) : (
                    modalFilteredStudents.map(s => {
                      const pendAmt = parseFloat(s.tuition_pending ?? s.financial_record?.tuition_pending ?? 0);
                      const deadline = s.payment_deadline || s.financial_record?.payment_deadline || 'N/A';
                      return (
                        <tr key={s.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '10px 12px', fontWeight: 700, color: '#0f172a' }}>
                            {s.first_name} {s.last_name}
                            <div style={{ fontSize: 11, color: '#64748b', fontWeight: 400 }}>@{s.username}</div>
                          </td>
                          <td style={{ padding: '10px 12px', color: '#334155' }}>
                            {s.profile?.course_display || s.profile?.course || 'MBBS'}
                            {s.profile?.student_id && <div style={{ fontSize: 11, color: '#64748b' }}>ID: {s.profile.student_id}</div>}
                          </td>
                          <td style={{ padding: '10px 12px', fontWeight: 400, color: '#991b1b' }}>
                            ₹{pendAmt.toLocaleString()}
                          </td>
                          <td style={{ padding: '10px 12px', color: '#64748b', fontSize: 12 }}>
                            {deadline}
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                            <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                              <button
                                onClick={() => {
                                  setFineTargetUser(s);
                                  setFineInput('');
                                }}
                                style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #cbd5e1', background: '#f8fafc', color: '#475569', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                              >
                                ➕ Add Fine
                              </button>
                              <button
                                onClick={() => {
                                  setShowOverdueModal(false);
                                  if (onPayStudent) {
                                    onPayStudent(s);
                                  }
                                }}
                                style={{ padding: '5px 12px', borderRadius: 6, border: 'none', background: '#16a34a', color: '#ffffff', fontSize: 11, fontWeight: 800, cursor: 'pointer' }}
                              >
                                💳 Pay
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Modal Footer */}
            <div style={{ padding: '12px 22px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', background: '#f8fafc' }}>
              <button
                onClick={() => setShowOverdueModal(false)}
                style={{ padding: '7px 16px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#ffffff', fontWeight: 700, fontSize: 12, cursor: 'pointer', color: '#334155' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── ADD FINE MODAL ── */}
      {fineTargetUser && (
        <div
          onClick={() => setFineTargetUser(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: '#ffffff', borderRadius: 14, width: '100%', maxWidth: 440, padding: 22, boxShadow: '0 20px 40px rgba(0,0,0,0.25)' }}
          >
            <h3 style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', margin: '0 0 4px' }}>➕ Add Overdue Fine Amount</h3>
            <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 16px' }}>
              Student: <strong>{fineTargetUser.first_name} {fineTargetUser.last_name}</strong>
            </p>

            <form onSubmit={handleAddFineSubmit}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 6 }}>Fine Amount (₹):</label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  required
                  placeholder="e.g. 500"
                  value={fineInput}
                  onChange={e => setFineInput(e.target.value)}
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14, fontWeight: 700 }}
                />
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 6 }}>
                  * Added to total pending tuition fee. Automatically included in collected revenue when paid.
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button
                  type="button"
                  onClick={() => setFineTargetUser(null)}
                  style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#ffffff', color: '#475569', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingFine}
                  style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: '#1d4ed8', color: '#ffffff', fontWeight: 800, fontSize: 12, cursor: 'pointer' }}
                >
                  {savingFine ? 'Adding Fine...' : 'Confirm & Add Fine'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── ADD FINE MODAL ── */}
      {fineTargetUser && (
        <div
          onClick={() => setFineTargetUser(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: '#ffffff', borderRadius: 14, width: '100%', maxWidth: 440, padding: 22, boxShadow: '0 20px 40px rgba(0,0,0,0.25)' }}
          >
            <h3 style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', margin: '0 0 4px' }}>➕ Add Overdue Fine Amount</h3>
            <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 16px' }}>
              Student: <strong>{fineTargetUser.first_name} {fineTargetUser.last_name}</strong>
            </p>

            <form onSubmit={handleAddFineSubmit}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 6 }}>Fine Amount (₹):</label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  required
                  placeholder="e.g. 500"
                  value={fineInput}
                  onChange={e => setFineInput(e.target.value)}
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14, fontWeight: 700 }}
                />
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 6 }}>
                  * Added to total pending tuition fee. Automatically included in collected revenue when paid.
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button
                  type="button"
                  onClick={() => setFineTargetUser(null)}
                  style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#ffffff', color: '#475569', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingFine}
                  style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: '#1d4ed8', color: '#ffffff', fontWeight: 800, fontSize: 12, cursor: 'pointer' }}
                >
                  {savingFine ? 'Adding Fine...' : 'Confirm & Add Fine'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── NET SURPLUS MONEY DEDUCTIONS MODAL ── */}
      {showDeductionsModal && (
        <div
          onClick={() => setShowDeductionsModal(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: '#ffffff', borderRadius: 16, width: '100%', maxWidth: 960, maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 50px rgba(0,0,0,0.25)', overflow: 'hidden' }}
          >
            {/* Modal Header with Top Right Total Amount Display */}
            <div style={{ padding: '16px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', flexWrap: 'wrap', gap: 12 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>💸 Money Deductions & Net Surplus Ledger</div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                  Operational costs, payroll payouts & Finance-approved vehicle expenses deducted from total revenue
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                {/* Top Right Side Total Amount Display */}
                <div style={{ fontSize: 13, fontWeight: 800, color: '#991b1b', background: '#fee2e2', padding: '6px 14px', borderRadius: 8, border: '1px solid #fecaca' }}>
                  Total Outflows: − ₹{filteredOutflowsTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>

                <button onClick={() => setShowDeductionsModal(false)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#94a3b8', lineHeight: 1 }}>×</button>
              </div>
            </div>

            {/* Filter Bar with Outflows Category Dropdown, From & To Date Pickers, and Search */}
            <div style={{ padding: '12px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, background: '#ffffff' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#334155' }}>Filter Outflow Category:</span>
                <select
                  value={deductionsFilter}
                  onChange={e => { setDeductionsFilter(e.target.value); setDeductionsPage(1); }}
                  style={{
                    padding: '6px 12px',
                    borderRadius: 8,
                    border: '1px solid #2563eb',
                    background: '#eff6ff',
                    color: '#1d4ed8',
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: 'pointer',
                    outline: 'none'
                  }}
                >
                  <option value="ALL">All Outflows ({dateFilteredAllCount})</option>
                  <option value="EXPENSE">Operational Expenses ({dateFilteredExpenseCount})</option>
                  <option value="PAYROLL">Payroll Payouts ({dateFilteredPayrollCount})</option>
                  <option value="VEHICLE_EXPENSE">Vehicle Expenses ({dateFilteredVehicleCount})</option>
                </select>
              </div>

              {/* Date Filters & Search Input */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>From:</span>
                <input
                  type="date"
                  value={deductionsFromDate}
                  onChange={e => { setDeductionsFromDate(e.target.value); setDeductionsPage(1); }}
                  style={{ padding: '5px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12 }}
                  title="From Date"
                />

                <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>To:</span>
                <input
                  type="date"
                  value={deductionsToDate}
                  onChange={e => { setDeductionsToDate(e.target.value); setDeductionsPage(1); }}
                  style={{ padding: '5px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12 }}
                  title="To Date"
                />

                {(deductionsFromDate || deductionsToDate) && (
                  <button
                    onClick={() => {
                      setDeductionsFromDate('');
                      setDeductionsToDate('');
                      setDeductionsPage(1);
                    }}
                    style={{
                      padding: '5px 10px',
                      borderRadius: 6,
                      border: '1px solid #fecaca',
                      background: '#fee2e2',
                      color: '#991b1b',
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: 'pointer'
                    }}
                    title="Clear selected date range"
                  >
                    ✕ Clear Dates
                  </button>
                )}

                <input
                  type="search"
                  placeholder="Search payee, vendor, category..."
                  value={deductionsSearch}
                  onChange={e => { setDeductionsSearch(e.target.value); setDeductionsPage(1); }}
                  style={{ padding: '6px 12px', border: '1px solid #cbd5e1', borderRadius: 7, fontSize: 12, width: 180 }}
                />
              </div>
            </div>

            {/* Table Body (10 records per page) */}
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 24px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, textAlign: 'left', marginTop: 12 }}>
                <thead>
                  <tr style={{ background: '#f8fafc', color: '#64748b', fontSize: 11, fontWeight: 500, textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0' }}>
                    <th style={{ padding: '10px 12px' }}>Outflow ID / Type</th>
                    <th style={{ padding: '10px 12px' }}>Date / Period</th>
                    <th style={{ padding: '10px 12px' }}>Payee / Vendor / Staff</th>
                    <th style={{ padding: '10px 12px' }}>Category / Description</th>
                    <th style={{ padding: '10px 12px' }}>Amount Deducted</th>
                    <th style={{ padding: '10px 12px' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedDeductions.length === 0 ? (
                    <tr>
                      <td colSpan="6" style={{ padding: 28, textAlign: 'center', color: '#94a3b8' }}>
                        No deduction transactions found matching the selected filter criteria.
                      </td>
                    </tr>
                  ) : (
                    pagedDeductions.map(d => (
                      <tr key={d.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '10px 12px', fontWeight: 400, color: '#334155' }}>
                          {d.id}
                          <div style={{ fontSize: 10, color: d.type === 'PAYROLL' ? '#1d4ed8' : '#7c3aed', fontWeight: 400 }}>
                            {d.type_label}
                          </div>
                        </td>
                        <td style={{ padding: '10px 12px', color: '#64748b', whiteSpace: 'nowrap' }}>{d.date}</td>
                        <td style={{ padding: '10px 12px', fontWeight: 400, color: '#475569' }}>{d.payee}</td>
                        <td style={{ padding: '10px 12px', color: '#475569' }}>
                          <span style={{ color: '#334155', fontWeight: 400 }}>{d.category}</span>
                          <div style={{ fontSize: 11, color: '#64748b' }}>{d.description}</div>
                        </td>
                        <td style={{ padding: '10px 12px', fontWeight: 400, color: '#991b1b' }}>
                          − ₹{d.amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td style={{ padding: '10px 12px' }}>
                          <span style={{ padding: '3px 8px', borderRadius: 12, fontSize: 10, fontWeight: 400, background: '#dcfce7', color: '#166534' }}>
                            {d.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Modal Footer with Pagination Controls */}
            <div style={{ padding: '12px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', flexWrap: 'wrap', gap: 10 }}>
              <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>
                {modalFilteredDeductions.length > 0 ? (
                  <>
                    Showing <strong>{((safeDeductionsPage - 1) * DEDUCTIONS_PER_PAGE) + 1}</strong> – <strong>{Math.min(safeDeductionsPage * DEDUCTIONS_PER_PAGE, modalFilteredDeductions.length)}</strong> of <strong>{modalFilteredDeductions.length}</strong> outflows
                  </>
                ) : (
                  '0 outflows'
                )}
              </div>

              {/* Pagination Controls */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button
                  disabled={safeDeductionsPage <= 1}
                  onClick={() => setDeductionsPage(p => Math.max(1, p - 1))}
                  style={{
                    padding: '5px 12px',
                    borderRadius: 6,
                    border: '1px solid #cbd5e1',
                    background: safeDeductionsPage <= 1 ? '#f1f5f9' : '#ffffff',
                    color: safeDeductionsPage <= 1 ? '#94a3b8' : '#334155',
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: safeDeductionsPage <= 1 ? 'not-allowed' : 'pointer'
                  }}
                >
                  ‹ Previous
                </button>

                <span style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', padding: '0 4px' }}>
                  Page {safeDeductionsPage} of {totalDeductionPages}
                </span>

                <button
                  disabled={safeDeductionsPage >= totalDeductionPages}
                  onClick={() => setDeductionsPage(p => Math.min(totalDeductionPages, p + 1))}
                  style={{
                    padding: '5px 12px',
                    borderRadius: 6,
                    border: '1px solid #cbd5e1',
                    background: safeDeductionsPage >= totalDeductionPages ? '#f1f5f9' : '#ffffff',
                    color: safeDeductionsPage >= totalDeductionPages ? '#94a3b8' : '#334155',
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: safeDeductionsPage >= totalDeductionPages ? 'not-allowed' : 'pointer'
                  }}
                >
                  Next ›
                </button>

                <button
                  onClick={() => setShowDeductionsModal(false)}
                  style={{ padding: '5px 14px', borderRadius: 6, border: '1px solid #cbd5e1', background: '#ffffff', fontWeight: 700, fontSize: 12, cursor: 'pointer', color: '#334155', marginLeft: 8 }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

            {/* ── ADD FINE MODAL ── */}
      {fineTargetUser && (
        <div
          onClick={() => setFineTargetUser(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: '#ffffff', borderRadius: 14, width: '100%', maxWidth: 440, padding: 22, boxShadow: '0 20px 40px rgba(0,0,0,0.25)' }}
          >
            <h3 style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', margin: '0 0 4px' }}>➕ Add Overdue Fine Amount</h3>
            <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 16px' }}>
              Student: <strong>{fineTargetUser.first_name} {fineTargetUser.last_name}</strong>
            </p>

            <form onSubmit={handleAddFineSubmit}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 6 }}>Fine Amount (₹):</label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  required
                  placeholder="e.g. 500"
                  value={fineInput}
                  onChange={e => setFineInput(e.target.value)}
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14, fontWeight: 700 }}
                />
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 6 }}>
                  * Added to total pending tuition fee. Automatically included in collected revenue when paid.
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button
                  type="button"
                  onClick={() => setFineTargetUser(null)}
                  style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#ffffff', color: '#475569', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingFine}
                  style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: '#1d4ed8', color: '#ffffff', fontWeight: 800, fontSize: 12, cursor: 'pointer' }}
                >
                  {savingFine ? 'Adding Fine...' : 'Confirm & Add Fine'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}

function max1(v) { return Math.max(1, v); }
export default FinanceDashboard;